import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../utils/api";

const useStickyState = (key, init) => {
  const [v, setV] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw != null ? JSON.parse(raw) : init;
    } catch {
      return init;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {}
  }, [key, v]);

  return [v, setV];
};

const useStickySet = (key, initSet = new Set()) => {
  const [raw, setRaw] = useStickyState(key, Array.from(initSet));
  const valueSet = useMemo(() => new Set(raw), [raw]);

  const setValue = (next) => {
    if (typeof next === "function") {
      setRaw((prev) => {
        const prevSet = new Set(prev);
        const out = next(prevSet);
        return Array.from(out instanceof Set ? out : new Set(out || []));
      });
    } else {
      setRaw(Array.from(next instanceof Set ? next : new Set(next || [])));
    }
  };

  return [valueSet, setValue];
};

const PROJ_SRC = "/data/nba/latest/projections.json";
const DATA_SRC = "/data/nba/latest/data.json";
const SITE_IDS = "/data/nba/latest/site_ids/site_ids.json";
const NAME_XWALK = "/data/nba/latest/name_xwalk.json";
const META_SRC = "/data/nba/latest/meta.json";

const SITES = {
  dk: {
    key: "dk",
    label: "DraftKings",
    logo: "/logos/dk.png",
    cap: 50000,
    slots: [
      { name: "PG", eligible: ["PG"] },
      { name: "SG", eligible: ["SG"] },
      { name: "SF", eligible: ["SF"] },
      { name: "PF", eligible: ["PF"] },
      { name: "C", eligible: ["C"] },
      { name: "G", eligible: ["PG", "SG"] },
      { name: "F", eligible: ["SF", "PF"] },
      { name: "UTIL", eligible: ["PG", "SG", "SF", "PF", "C"] },
    ],
    exportHeader: ["PG", "SG", "SF", "PF", "C", "G", "F", "UTIL"],
    posFields: ["DK Pos", "Pos", "DK Position", "dk_pos", "position"],
    projFields: ["DK Proj", "Projection", "Points", "dk_proj", "proj"],
    salFieldsProj: ["DK Salary", "dk_salary"],
    salFieldsData: ["DK Sal", "DK Salary", "dk_sal", "dk_salary"],
    pownFields: [
      "DK pOWN%", "DK pOWN", "DK Ownership%", "DK Ownership",
      "dk_pown", "dk_pown_pct", "dk_own", "dk_own_pct"
    ],
    usgFields: [
      "DK USG%", "DK USG", "USG%", "USG", "Usage%", "Usage",
      "Proj USG", "Projected Usage", "DK Usage%", "DK Usage",
      "dk_usg", "dk_usg_pct", "usage_pct", "usage", "projected_usage"
    ],
    minFields: [
      "DK Min", "Minutes", "Min", "Mins", "Proj Min", "Projected Minutes",
      "DK Minutes", "dk_min", "dk_minutes", "minutes", "mins", "projected_minutes"
    ],
  },

  fd: {
    key: "fd",
    label: "FanDuel",
    logo: "/logos/fd.png",
    cap: 60000,
    slots: [
      { name: "PG", eligible: ["PG"] },
      { name: "SG", eligible: ["SG"] },
      { name: "SF", eligible: ["SF"] },
      { name: "PF", eligible: ["PF"] },
      { name: "C", eligible: ["C"] },
      { name: "G/F", eligible: ["PG", "SG", "SF", "PF"] },
      { name: "F/C", eligible: ["SF", "PF", "C"] },
      { name: "UTIL1", eligible: ["PG", "SG", "SF", "PF", "C"] },
      { name: "UTIL2", eligible: ["PG", "SG", "SF", "PF", "C"] },
    ],
    exportHeader: ["PG", "SG", "SF", "PF", "C", "G/F", "F/C", "UTIL", "UTIL"],
    posFields: ["FD Pos", "Pos", "FD Position", "fd_pos", "position"],
    projFields: ["FD Proj", "Projection", "Points", "fd_proj", "proj"],
    salFieldsProj: ["FD Salary", "fd_salary"],
    salFieldsData: ["FD Sal", "FD Salary", "fd_sal", "fd_salary"],
    pownFields: [
      "FD pOWN%", "FD pOWN", "FD Ownership%", "FD Ownership",
      "fd_pown", "fd_pown_pct", "fd_own", "fd_own_pct"
    ],
    usgFields: [
      "FD USG%", "FD USG", "USG%", "USG", "Usage%", "Usage",
      "Proj USG", "Projected Usage", "FD Usage%", "FD Usage",
      "fd_usg", "fd_usg_pct", "usage_pct", "usage", "projected_usage"
    ],
    minFields: [
      "FD Min", "Minutes", "Min", "Mins", "Proj Min", "Projected Minutes",
      "FD Minutes", "fd_min", "fd_minutes", "minutes", "mins", "projected_minutes"
    ],
  },
};

const ORDER_DK = ["PG", "SG", "SF", "PF", "C", "G", "F", "UTIL"];
const ORDER_FD = ["PG", "SG", "SF", "PF", "C", "G/F", "F/C", "UTIL1", "UTIL2"];
const MAX_SAVED_BUILDS = 12;

const clamp = (v, lo = 0, hi = 1e9) =>
  Math.max(lo, Math.min(hi, Number.isFinite(+v) ? +v : lo));

const isValidNbaSalary = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 3000 && n <= 20000;
};

const canonKey = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[%]/g, " pct ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const pickFirst = (row, keys) => {
  if (!row || typeof row !== "object") return undefined;

  for (const k of keys) {
    if (row[k] != null && row[k] !== "") return row[k];
  }

  const keyMap = new Map();
  for (const [rk, rv] of Object.entries(row)) {
    keyMap.set(canonKey(rk), rv);
  }

  for (const k of keys) {
    const v = keyMap.get(canonKey(k));
    if (v != null && v !== "") return v;
  }

  return undefined;
};

const upper = (s) => String(s || "").toUpperCase().trim();

const stripAccents = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const normalizeName = (name) => {
  let n = stripAccents(String(name || "").toLowerCase());
  n = n.replace(/[.,'’"-]/g, " ").replace(/\s+/g, " ").trim();
  n = n.replace(/\b[a-z]\b/g, "").replace(/\s{2,}/g, " ").trim();
  n = n.replace(/\b(jr|jr\.|sr|sr\.|ii|iii|iv|v)\b/g, "").replace(/\s{2,}/g, " ").trim();
  return n;
};

const splitPos = (s) =>
  String(s || "")
    .toUpperCase()
    .replace(/\s+/g, "")
    .split("/")
    .filter(Boolean);


const normalizeTeamAbbr = (team) => {
  const t = upper(team);
  if (t === "SA") return "SAS";
  if (t === "PHO") return "PHX";
  if (t === "NY") return "NYK";
  if (t === "GS") return "GSW";
  return t;
};

const getSiteBucket = (siteIds, siteKey) => {
  const root = siteIds || {};
  return (
    root?.site_ids?.[siteKey] ||
    root?.sites?.[siteKey] ||
    root?.[siteKey] ||
    {}
  );
};

const siteBucketToRecords = (bucket) => {
  if (Array.isArray(bucket)) {
    return bucket
      .map((rec) => ({
        name: String(pickFirst(rec, ["name", "player", "Player", "Name", "displayName"]) || "").trim(),
        rec: rec || {},
      }))
      .filter((x) => x.name);
  }

  if (bucket && typeof bucket === "object") {
    return Object.entries(bucket)
      .map(([name, rec]) => ({
        name: String(pickFirst(rec, ["name", "player", "Player", "Name", "displayName"]) || name || "").trim(),
        rec: rec || {},
      }))
      .filter((x) => x.name);
  }

  return [];
};

function toHhMm(timeStr) {
  if (!timeStr) return "";

  const raw = String(timeStr).trim();
  if (!raw) return "";

  // ISO-ish / datetime strings
  const parsedDate = new Date(raw);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  let s = raw.toUpperCase().replace(/\s+/g, " ").trim();
  s = s.replace(/\bET\b|\bEST\b|\bEDT\b|\bCT\b|\bCST\b|\bCDT\b|\bMT\b|\bMST\b|\bMDT\b|\bPT\b|\bPST\b|\bPDT\b/g, "").trim();

  // 7 PM / 7:00 PM / 7:00PM
  let m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);
  if (m) {
    let hh = +m[1];
    const mm = +(m[2] || 0);
    const ap = m[3];
    if (ap === "AM") {
      if (hh === 12) hh = 0;
    } else {
      if (hh !== 12) hh += 12;
    }
    const d = new Date();
    d.setHours(hh, mm, 0, 0);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }

  // 19:00 / 19:00:00
  m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m) {
    const hh = +m[1];
    const mm = +m[2];
    const d = new Date();
    d.setHours(hh, mm, 0, 0);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }

  // 1900 / 0700
  m = s.match(/^(\d{2})(\d{2})$/);
  if (m) {
    const hh = +m[1];
    const mm = +m[2];
    const d = new Date();
    d.setHours(hh, mm, 0, 0);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }

  return raw;
}

function toMinutesOfDay(str) {
  const raw = String(str || "").trim();
  if (!raw) return null;

  // First try Date parsing for ISO/datetime values
  const parsedDate = new Date(raw);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.getHours() * 60 + parsedDate.getMinutes();
  }

  let s = raw.toUpperCase().replace(/\s+/g, " ").trim();
  s = s.replace(/\bET\b|\bEST\b|\bEDT\b|\bCT\b|\bCST\b|\bCDT\b|\bMT\b|\bMST\b|\bMDT\b|\bPT\b|\bPST\b|\bPDT\b/g, "").trim();

  // 7 PM / 7:00 PM / 7:00PM
  let m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);
  if (m) {
    let h = +m[1];
    const min = +(m[2] || 0);
    const ap = m[3];

    if (ap === "AM") {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h += 12;
    }

    return h * 60 + min;
  }

  // 19:00 / 19:00:00
  m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m) {
    return (+m[1]) * 60 + (+m[2]);
  }

  // 1900 / 0700
  m = s.match(/^(\d{2})(\d{2})$/);
  if (m) {
    return (+m[1]) * 60 + (+m[2]);
  }

  return null;
}

function sameDayHasStarted(timeStr) {
  const raw = String(timeStr || "").trim();
  if (!raw) return false;

  const parsedDate = new Date(raw);
  if (!Number.isNaN(parsedDate.getTime())) {
    return Date.now() >= parsedDate.getTime();
  }

  const mins = toMinutesOfDay(raw);
  if (mins == null) return false;

  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  return nowMins >= mins;
}

function csvToRows(text) {
  const out = [];
  let i = 0;
  let cur = "";
  let row = [];
  let inQuotes = false;

  while (i < text.length) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cur += '"';
        i += 2;
        continue;
      }
      if (ch === '"') {
        inQuotes = false;
        i += 1;
        continue;
      }
      cur += ch;
      i += 1;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      row.push(cur);
      cur = "";
      i += 1;
      continue;
    }
    if (ch === "\n") {
      row.push(cur);
      out.push(row);
      row = [];
      cur = "";
      i += 1;
      continue;
    }
    if (ch === "\r") {
      i += 1;
      continue;
    }

    cur += ch;
    i += 1;
  }

  row.push(cur);
  out.push(row);

  return out.filter((r) => r.some((x) => String(x || "").trim() !== ""));
}

function toCsv(rows) {
  return rows
    .map((r) =>
      r
        .map((v) => {
          const s = String(v ?? "");
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(",")
    )
    .join("\n");
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function useJson(url) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        const j = ct.includes("json") ? await r.json() : JSON.parse(await r.text());
        if (alive) setData(j);
      } catch {
        if (alive) setData(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);

  return { data };
}

function useLastUpdated(dataUrl) {
  const [d, setD] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(META_SRC + `?_=${Date.now()}`, { cache: "no-store" });
        if (r.ok) {
          const j = await r.json();
          const arts = Array.isArray(j?.artifacts) ? j.artifacts : [];
          const want = String(dataUrl || "").split("/").pop();
          const art =
            arts.find((a) => a?.path === dataUrl) ||
            arts.find((a) => String(a?.latest || "").replace(/\\/g, "/").endsWith(dataUrl)) ||
            arts.find((a) => String(a?.path || "").split("/").pop() === want) ||
            arts.find((a) => String(a?.latest || "").split("/").pop() === want) ||
            null;

          const iso =
            art?.last_updated ||
            art?.tags?.last_updated ||
            j?.last_updated ||
            j?.generated_at ||
            null;

          if (alive && iso) {
            setD(new Date(iso));
            return;
          }
        }
      } catch {}

      try {
        const h = await fetch(dataUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified") || h.headers.get("date");
        if (alive && lm) {
          setD(new Date(lm));
          return;
        }
      } catch {}

      if (alive) setD(null);
    })();

    return () => {
      alive = false;
    };
  }, [dataUrl]);

  return d;
}

function fmtUpdated(d) {
  return d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
}

function numFromMaybePercent(v) {
  if (v == null || v === "") return 0;
  const s = String(v).trim().replace(/[,%]/g, "");
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return clamp(n, 0, 100);
}

function normalizePctValue(raw) {
  if (raw == null || raw === "") return "";
  const n = Number(String(raw).replace(/[,% ]/g, ""));
  if (!Number.isFinite(n)) return "";
  return clamp(n, 0, 100);
}

const TeamBadge = ({ team, isDark }) => {
  const colors = {
    ATL: "#E03A3E", BOS: "#007A33", BKN: "#000000", CHA: "#1D1160", CHI: "#CE1141",
    CLE: "#6F263D", DAL: "#00538C", DEN: "#0E2240", DET: "#C8102E", GSW: "#1D428A",
    HOU: "#CE1141", IND: "#002D62", LAC: "#C8102E", LAL: "#552583", MEM: "#5D76A9",
    MIA: "#98002E", MIL: "#00471B", MIN: "#0C2340", NOP: "#0C2340", NYK: "#F58426",
    OKC: "#007AC1", ORL: "#0077C0", PHI: "#006BB6", PHX: "#1D1160", POR: "#E03A3E",
    SAC: "#5A2D81", SAS: "#C4CED4", TOR: "#CE1141", UTA: "#002B5C", WAS: "#002B5C",
  };

  const bg = (colors[team] || "#64748b").toLowerCase();

  const hexToRGB = (hex) => {
    const h = String(hex || "#888").replace("#", "");
    if (h.length !== 6) return { r: 136, g: 136, b: 136 };
    const v = parseInt(h, 16);
    return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 };
  };

  const readableText = (hex) => {
    const { r, g, b } = hexToRGB(hex);
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return L < 140 ? "#FFFFFF" : "#111111";
  };

  const fg = readableText(bg);

  return (
    <span
      className="inline-flex items-center justify-center rounded-md px-2 py-[2px] text-[11px] font-semibold"
      style={{
        backgroundColor: bg,
        color: fg,
        minWidth: 34,
        letterSpacing: "0.02em",
        boxShadow: isDark
          ? "0 0 0 1px rgba(15,23,42,0.45), 0 0 0 2px rgba(255,255,255,0.06)"
          : "0 0 0 1px rgba(15,23,42,0.10)",
      }}
      title={team}
    >
      {team}
    </span>
  );
};

const teamLogo = (abbr) => `/logos/nba/${abbr}.png`;

function canonicalSlotOrder(site) {
  return site === "dk" ? ORDER_DK : ORDER_FD;
}

function expandHeaderToCanonical(site, headerCells) {
  const header = (headerCells || []).map((x) => String(x || "").trim().toUpperCase());
  if (site === "dk") return ORDER_DK;

  const seen = {};
  return header.map((h) => {
    seen[h] = (seen[h] || 0) + 1;
    return h === "UTIL" ? `UTIL${seen[h]}` : h;
  });
}

function parseExportCell(site, cell) {
  const raw = String(cell || "").trim();
  if (!raw) return { name: "", playerId: "", slateCode: "", lockedTag: false };

  if (site === "dk") {
    let s = raw;
    let lockedTag = false;

    // Handle DK live cells like: Devin Booker (42261030) (LOCKED)
    if (/\(LOCKED\)\s*$/i.test(s)) {
      lockedTag = true;
      s = s.replace(/\(LOCKED\)\s*$/i, "").trim();
    }

    // DK standard: Name (ID)
    const m = s.match(/^(.*?)\s*\((\d+)\)\s*$/);
    if (m) {
      return {
        name: m[1].trim(),
        playerId: m[2].trim(),
        slateCode: "",
        lockedTag,
      };
    }

    // DK id only
    if (/^\d+$/.test(s)) {
      return { name: "", playerId: s, slateCode: "", lockedTag };
    }

    // DK plain name fallback
    return { name: s, playerId: "", slateCode: "", lockedTag };
  }

  // ---------------- FD ----------------
  let s = raw;
  let lockedTag = false;

  if (/\(LOCKED\)\s*$/i.test(s)) {
    lockedTag = true;
    s = s.replace(/\(LOCKED\)\s*$/i, "").trim();
  }

  // FD standard: slate-playerId:Player Name
  const colonIdx = s.indexOf(":");
  if (colonIdx >= 0) {
    const left = s.slice(0, colonIdx).trim();
    const name = s.slice(colonIdx + 1).trim();

    const dashIdx = left.lastIndexOf("-");
    if (dashIdx >= 0) {
      return {
        name,
        slateCode: left.slice(0, dashIdx).trim(),
        playerId: left.slice(dashIdx + 1).trim(),
        lockedTag,
      };
    }

    return {
      name,
      slateCode: "",
      playerId: left,
      lockedTag,
    };
  }

  // FD live format: slate-playerId
  const slateIdOnly = s.match(/^(.+)-(\d+)$/);
  if (slateIdOnly) {
    return {
      name: "",
      slateCode: slateIdOnly[1].trim(),
      playerId: slateIdOnly[2].trim(),
      lockedTag,
    };
  }

  // FD id only
  if (/^\d+$/.test(s)) {
    return {
      name: "",
      playerId: s,
      slateCode: "",
      lockedTag,
    };
  }

  // FD plain name
  return { name: s, playerId: "", slateCode: "", lockedTag };
}

function formatExportCell(site, item) {
  const name = String(item?.name || "").trim();
  const playerId = String(item?.playerId || "").trim();
  const slateCode = String(item?.slateCode || "").trim();
  if (!name) return "";

  if (site === "dk") {
    return playerId ? `${name} (${playerId})` : name;
  }

  const left = [slateCode, playerId].filter(Boolean).join("-");
  return left ? `${left}:${name}` : name;
}

function getHeaderIndex(header, names) {
  const map = new Map((header || []).map((h, i) => [canonKey(h), i]));
  for (const n of names) {
    const idx = map.get(canonKey(n));
    if (idx != null) return idx;
  }
  return -1;
}

function getContestSlotIndexes(headerRaw, site) {
  const hdr = (headerRaw || []).map((x) => String(x || "").trim().toUpperCase());
  const wantedBase = site === "dk"
    ? ["PG", "SG", "SF", "PF", "C", "G", "F", "UTIL"]
    : ["PG", "SG", "SF", "PF", "C", "G/F", "F/C", "UTIL", "UTIL"];

  const used = new Set();
  const indexes = [];

  for (const want of wantedBase) {
    let found = -1;
    for (let i = 0; i < hdr.length; i++) {
      if (used.has(i)) continue;
      if (hdr[i] === want) {
        found = i;
        break;
      }
    }
    if (found < 0) return null;
    used.add(found);
    indexes.push(found);
  }

  return indexes;
}

function parseUploadedCsv(text, site) {
  const rows = csvToRows(text);
  if (!rows.length) {
    throw new Error("CSV is empty.");
  }

  const headerRaw = rows[0] || [];
  const cleanHeader = headerRaw.map((x) => String(x || "").trim());
  const expectedHeader = SITES[site].exportHeader;
  const isLineups =
    cleanHeader.length >= expectedHeader.length &&
    expectedHeader.every(
      (v, i) => String(cleanHeader[i] || "").trim().toUpperCase() === v.toUpperCase()
    );

  if (isLineups) {
    const expected = canonicalSlotOrder(site);
    const lineups = rows.slice(1).map((r, idx) => {
      const assignment = expected.map((slot, i) => {
        const parsed = parseExportCell(site, r[i] || "");
        return {
          slot,
          originalCell: r[i] || "",
          name: parsed.name,
          playerId: parsed.playerId,
          slateCode: parsed.slateCode,
        };
      });
      return {
        index: idx + 1,
        id: `uploaded_${idx + 1}`,
        sourceRowIndex: idx + 1,
        assignment,
      };
    });

    return {
      kind: "lineups",
      header: headerRaw,
      rawRows: rows,
      lineups,
      contests: [],
      selectedContestName: "",
      contestNameIdx: -1,
      entryIdIdx: -1,
      slotIndexes: expected.map((_, i) => i),
    };
  }

  const entryIdIdx = getHeaderIndex(headerRaw, ["Entry ID", "EntryId", "Entry"]);
  const contestNameIdx = getHeaderIndex(headerRaw, ["Contest Name", "Contest"]);
  const slotIndexes = getContestSlotIndexes(headerRaw, site);

  if (contestNameIdx >= 0 && slotIndexes) {
    const expected = canonicalSlotOrder(site);
    const byContest = new Map();

    rows.slice(1).forEach((r, dataIdx) => {
      const sourceRowIndex = dataIdx + 1;
      const entryId = entryIdIdx >= 0 ? String(r[entryIdIdx] || "").trim() : "";
      const contestNameRaw = String(r[contestNameIdx] || "").trim();
      const hasAnyLineupCell = slotIndexes.some((idx) => String(r[idx] || "").trim() !== "");

      // Skip non-entry rows like FD template player-list / blank rows
      if (!entryId || !contestNameRaw || !hasAnyLineupCell) return;

      const contestName = contestNameRaw;

      const assignment = expected.map((slot, j) => {
        const cellIdx = slotIndexes[j];
        const raw = r[cellIdx] || "";
        const parsed = parseExportCell(site, raw);
        return {
          slot,
          originalCell: raw,
          name: parsed.name,
          playerId: parsed.playerId,
          slateCode: parsed.slateCode,
        };
      });

      const lineup = {
        index: 0,
        id: `contest_${sourceRowIndex}_${entryId}`,
        sourceRowIndex,
        entryId,
        contestName,
        assignment,
      };

      if (!byContest.has(contestName)) byContest.set(contestName, []);
      byContest.get(contestName).push(lineup);
    });

    const contests = Array.from(byContest.entries())
      .map(([name, list]) => ({
        name,
        count: list.length,
        lineups: list.map((x, i) => ({ ...x, index: i + 1 })),
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    return {
      kind: "contest",
      header: headerRaw,
      rawRows: rows,
      contests,
      selectedContestName: contests[0]?.name || "",
      contestNameIdx,
      entryIdIdx,
      slotIndexes,
      lineups: contests[0]?.lineups || [],
    };
  }

  throw new Error(
    `Could not detect CSV type for ${site.toUpperCase()}. Upload either a plain lineup export or a contest entries CSV.`
  );
}

async function runLateSwapBackend(payload) {
  const res = await fetch(`${API_BASE}/solve_nba_late_swap`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`Late swap failed: HTTP ${res.status}`);
  }

  const data = await res.json();
  if (!data?.success) {
    throw new Error(data?.reason || "No feasible late swap");
  }

  return data;
}

function normalizeBackendAssignment(site, assignment) {
  const order = canonicalSlotOrder(site);
  const bySlot = new Map(
    (assignment || []).map((x) => [String(x.slot || "").trim(), String(x.name || "").trim()])
  );
  return order.map((slot) => ({
    slot,
    name: bySlot.get(slot) || "",
  }));
}

function positionMatchesFilter(posList, posFilter) {
  if (posFilter === "ALL") return true;
  const base = posFilter.replace(/[12]$/, "");
  if (base === "G") return posList.includes("PG") || posList.includes("SG");
  if (base === "F") return posList.includes("SF") || posList.includes("PF");
  if (base === "G/F") return posList.some((p) => ["PG", "SG", "SF", "PF"].includes(p));
  if (base === "F/C") return posList.some((p) => ["SF", "PF", "C"].includes(p));
  if (base === "UTIL") return true;
  return posList.includes(base);
}

function buildLateSwapPayload({
  site,
  lineup,
  openSlots,
  lockedAssignment,
  eligiblePlayers,
  remainingSalary,
  boosts,
  randomness,
  maxFromTeam,
  lineupPownMax,
  maxPlayerPown,
  excludes,
  locks,
}) {
  return {
    site,
    cap_remaining: remainingSalary,
    open_slots: openSlots.map((s) => s.name),
    locked_assignment: lockedAssignment.map((x) => ({ slot: x.slot, name: x.name })),
    players: eligiblePlayers.map((p) => ({
      name: p.name,
      pos: p.posDisp,
      team: p.team,
      opp: p.opp,
      salary: p.salary,
      proj: p.eproj,
      pown: p.pown || 0,
      usg: p.usg || 0,
      minutes: p.minutes || 0,
    })),
    objective: "proj",
    boosts,
    randomness: Number(randomness) || 0,
    time_limit_ms: 1500,
    max_from_team:
      String(maxFromTeam).trim() === "" ? null : Math.max(1, Number(maxFromTeam) || 1),
    lineup_pown_max:
      String(lineupPownMax).trim() === "" ? null : Number(lineupPownMax),
    max_player_pown:
      String(maxPlayerPown).trim() === "" ? null : Number(maxPlayerPown),
    excludes: Array.from(excludes || []),
    locks: Array.from(locks || []),
    original_lineup: lineup.assignment.map((x) => ({ slot: x.slot, name: x.name })),
  };
}

function buildUsageTables(lineups) {
  const playerCounts = new Map();
  const teamCounts = new Map();
  const totalLineups = Math.max(lineups?.length || 0, 1);

  for (const lineup of lineups || []) {
    const arr = lineup.finalAssignment || lineup.assignment || [];
    for (const a of arr) {
      const nm = String(a?.name || "").trim();
      if (!nm) continue;

      playerCounts.set(nm, {
        name: nm,
        team: a?.team || "",
        pos: a?.posDisp || "",
        count: (playerCounts.get(nm)?.count || 0) + 1,
      });

      const tm = String(a?.team || "").trim();
      if (tm) {
        teamCounts.set(tm, {
          team: tm,
          count: (teamCounts.get(tm)?.count || 0) + 1,
        });
      }
    }
  }

  const players = Array.from(playerCounts.values())
    .map((x) => ({
      ...x,
      exposure: (x.count / totalLineups) * 100,
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const teams = Array.from(teamCounts.values())
    .map((x) => ({
      ...x,
      exposure: (x.count / (totalLineups * 8)) * 100,
    }))
    .sort((a, b) => b.count - a.count || a.team.localeCompare(b.team));

  return { players, teams };
}

export default function NbaLateSwap({ activeTab = "lateSwap", setActiveTab = () => {} }) {
  const { data: proj } = useJson(PROJ_SRC);
  const { data: dataRows } = useJson(DATA_SRC);
  const { data: siteIds } = useJson(SITE_IDS);
  const { data: nameXwalk } = useJson(NAME_XWALK);
  const updatedAt = useLastUpdated(PROJ_SRC);

  const [hideExcluded, setHideExcluded] = useState(false);
  const [theme, setTheme] = useStickyState("nba.lateswap.theme", "dark");
  const isDark = theme !== "light";

  const [site, setSite] = useStickyState("nba.lateswap.site", "dk");
  const [slate, setSlate] = useStickyState("nba.lateswap.slate", "main");
  const CFG = SITES[site];
  const panelClass = "rounded-2xl border shadow-sm " + (isDark ? "border-slate-800 bg-slate-900/95" : "border-slate-200 bg-white");
  const tabWrapClass = "inline-flex shrink-0 items-center gap-6 border-b " + (isDark ? "border-slate-800" : "border-slate-200");
  const navTabClass = (selected) => "relative inline-flex h-9 items-center gap-2 whitespace-nowrap pb-2 text-sm font-semibold transition " + (selected ? (isDark ? "text-white" : "text-slate-900") + " after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-sky-400" : "text-slate-500 hover:text-sky-500");


  const [csvName, setCsvName] = useStickyState("nba.lateswap.csvName", "");
  const [parseError, setParseError] = useStickyState("nba.lateswap.parseError", "");
  const [uploadBundle, setUploadBundle] = useState(null);
  const [selectedContestName, setSelectedContestName] = useStickyState("nba.lateswap.selectedContestName", "");
  const [importedLineups, setImportedLineups] = useStickyState("nba.lateswap.importedLineups", []);
  const [swappedLineups, setSwappedLineups] = useStickyState("nba.lateswap.swappedLineups", []);
  const [savedBuilds, setSavedBuilds] = useStickyState("nba.lateswap.savedBuilds", []);
  const [lockedGames, setLockedGames] = useStickySet("nba.lateswap.lockedGames", new Set());
  const [running, setRunning] = useState(false);
  const [useAutoStarted, setUseAutoStarted] = useStickyState("nba.lateswap.useAutoStarted", true);
  const [showOnlyChanged, setShowOnlyChanged] = useStickyState("nba.lateswap.showOnlyChanged", false);
  const [statusMsg, setStatusMsg] = useStickyState("nba.lateswap.statusMsg", "");
  const fileRef = useRef(null);

  const [q, setQ] = useStickyState("nba.lateswap.q", "");
  const [posFilter, setPosFilter] = useStickyState("nba.lateswap.posFilter", "ALL");
  const [sort, setSort] = useStickyState("nba.lateswap.sort", { col: "eproj", dir: "desc" });

  const [locks, setLocks] = useStickySet("nba.lateswap.locks", new Set());
  const [excls, setExcls] = useStickySet("nba.lateswap.excls", new Set());
  const [boost, setBoost] = useStickyState("nba.lateswap.boost", {});
  const [teamBoost, setTeamBoost] = useStickyState("nba.lateswap.teamBoost", {});
  const [projDraft, setProjDraft] = useState({});
  useEffect(() => { setProjDraft({}); }, [site, slate]);
  const bumpTeamBoost = (team, step) => setTeamBoost((prev) => ({ ...prev, [team]: clamp((Number(prev[team]) || 0) + step, -6, 6) }));

  const [customProj, setCustomProj] = useStickyState(`nba.lateswap.${site}.${slate}.customProj`, {});
  const [unprojectedAllows, setUnprojectedAllows] = useStickySet(`nba.lateswap.${site}.${slate}.unprojectedAllows`, new Set());
  const [showAllPlayers, setShowAllPlayers] = useStickyState(`nba.lateswap.${site}.${slate}.showAllPlayers`, false);
  const [minExp, setMinExp] = useStickyState("nba.lateswap.minExp", {});
  const [maxExp, setMaxExp] = useStickyState("nba.lateswap.maxExp", {});

  const [randomness, setRandomness] = useStickyState("nba.lateswap.randomness", 0);
  const [maxFromTeam, setMaxFromTeam] = useStickyState("nba.lateswap.maxFromTeam", "");
  const [lineupPownMax, setLineupPownMax] = useStickyState("nba.lateswap.lineupPownMax", "");
  const [maxPlayerPown, setMaxPlayerPown] = useStickyState("nba.lateswap.maxPlayerPown", "");

  const xwalk = useMemo(() => {
    const m = new Map();
    if (nameXwalk && typeof nameXwalk === "object") {
      for (const [k, v] of Object.entries(nameXwalk)) {
        m.set(normalizeName(k), normalizeName(v));
      }
    }
    return m;
  }, [nameXwalk]);

  const aliasKey = (name) => {
    const base = normalizeName(name);
    return xwalk.get(base) || base;
  };

  const dataByName = useMemo(() => {
    const arr = Array.isArray(dataRows) ? dataRows : [];
    const m = new Map();

    for (const r of arr) {
      const raw =
        pickFirst(r, ["Player", "PLAYER", "player", "Name", "name"]) || "";
      if (!raw) continue;

      const k = aliasKey(raw);

      m.set(k, {
        time: toHhMm(pickFirst(r, ["Time", "time", "Start Time", "Game Time"]) || ""),
        dkSal: pickFirst(r, ["DK Sal", "DK Salary", "dk_sal", "dk_salary"]),
        fdSal: pickFirst(r, ["FD Sal", "FD Salary", "fd_sal", "fd_salary"]),
        team: pickFirst(r, ["Team", "TEAM", "team", "team_abbr"]),
        opp: pickFirst(r, ["Opp", "OPP", "opp", "opponent"]),
        pos: pickFirst(r, ["Pos", "Position", "DK Pos", "FD Pos", "pos", "position"]),
        usg: pickFirst(r, [
          "DK USG%", "FD USG%", "USG%", "USG", "Usage%", "Usage",
          "Proj USG", "Projected Usage", "usage_pct", "usage",
          "dk_usg", "fd_usg", "dk_usg_pct", "fd_usg_pct"
        ]),
        minutes: pickFirst(r, [
          "DK Min", "FD Min", "Minutes", "Min", "Mins",
          "Proj Min", "Projected Minutes", "minutes", "mins",
          "dk_min", "fd_min"
        ]),
      });
    }

    return m;
  }, [dataRows, xwalk]);

  const siteIdMaps = useMemo(() => {
    const build = (siteKey) => {
      const byName = new Map();
      const byId = new Map();
      for (const { name, rec } of siteBucketToRecords(getSiteBucket(siteIds, siteKey))) {
        const normalizedRec = { ...rec, __name: name };
        byName.set(aliasKey(name), normalizedRec);
        byName.set(name, normalizedRec);

        const ids = [
          rec?.id,
          rec?.ID,
          rec?.main_id,
          rec?.MAIN_ID,
          rec?.player_id,
          rec?.PlayerID,
          rec?.dkid,
          rec?.fdid,
          rec?.fid,
          rec?.late_id,
          rec?.LateID,
          rec?.LATE_ID,
        ]
          .map((x) => String(x || "").trim())
          .filter(Boolean);

        for (const id of ids) byId.set(id, normalizedRec);
      }
      return { byName, byId, records: siteBucketToRecords(getSiteBucket(siteIds, siteKey)) };
    };

    return { dk: build("dk"), fd: build("fd") };
  }, [siteIds, xwalk]);

  const siteIdsByName = useMemo(() => siteIdMaps?.[site]?.byName || new Map(), [siteIdMaps, site]);

  const getSiteId = (name) => {
    const rec = siteIdsByName.get(aliasKey(name)) || siteIdsByName.get(name) || {};
    const useLate = slate === "late";
    const primaryId = useLate
      ? rec.late_id ?? rec.LateID ?? rec.LATE_ID ?? ""
      : rec.id ?? rec.ID ?? rec.main_id ?? rec.MAIN_ID ?? "";
    const id =
      primaryId ??
      (useLate ? "" : rec.player_id ?? rec.dkid ?? rec.fdid ?? rec.fid ?? rec.PlayerID ?? "");

    const fullId = String(id || "").trim();
    const dashIdx = fullId.lastIndexOf("-");
    const slateCodeFromId = dashIdx > 0 ? fullId.slice(0, dashIdx) : "";
    const slateCode = rec.slate_id ?? rec.slate ?? rec.SLATE_ID ?? slateCodeFromId ?? "";
    return { id: fullId, slate: String(slateCode || "") };
  };

  const baseRows = useMemo(() => {
    const projectedBase = Array.isArray(proj?.rows)
      ? proj.rows
      : Array.isArray(proj?.players)
      ? proj.players
      : Array.isArray(proj?.data)
      ? proj.data
      : Array.isArray(proj)
      ? proj
      : [];

    const rowsByKey = new Map();
    const siteKey = site;
    const activeSiteRecords = siteIdMaps?.[siteKey]?.records || [];
    const activeSiteByName = siteIdMaps?.[siteKey]?.byName || new Map();
    const dkSiteByName = siteIdMaps?.dk?.byName || new Map();

    const getSlateParts = (rec) => {
      const mainId = rec?.id ?? rec?.ID ?? rec?.main_id ?? rec?.MAIN_ID ?? rec?.player_id ?? rec?.PlayerID ?? null;
      const mainSalaryId = rec?.salary ?? rec?.main_salary ?? rec?.MAIN_SALARY ?? null;
      const lateId = rec?.late_id ?? rec?.LateID ?? rec?.LATE_ID ?? null;
      const lateSalaryId = rec?.late_salary ?? rec?.LATE_SALARY ?? null;

      const validMainSalary = isValidNbaSalary(mainSalaryId);
      const validLateSalary = isValidNbaSalary(lateSalaryId);
      const hasMain = !!mainId && validMainSalary;
      const hasLate = !!lateId && validLateSalary;

      let slateTag = null;
      if (hasMain && hasLate) slateTag = "both";
      else if (hasLate) slateTag = "late";
      else if (hasMain) slateTag = "main";

      const salary = slate === "late"
        ? (validLateSalary ? Number(lateSalaryId) : null)
        : (validMainSalary ? Number(mainSalaryId) : null);

      const id = slate === "late" ? lateId : mainId;
      return { slateTag, salary, id: String(id || "").trim() };
    };

    const makeRow = ({ r = {}, nameRaw, idx, siteOnly = false }) => {
      if (!nameRaw) return null;
      const key = aliasKey(nameRaw);
      const fromData = dataByName.get(key) || {};
      const rec = activeSiteByName.get(key) || activeSiteByName.get(nameRaw) || {};
      const dkRec = dkSiteByName.get(key) || dkSiteByName.get(nameRaw) || {};
      const slateInfo = getSlateParts(rec);

      const rawPos =
        pickFirst(r, CFG.posFields) ??
        rec?.pos ?? rec?.Pos ?? rec?.position ?? rec?.Position ??
        dkRec?.pos ?? dkRec?.Pos ?? dkRec?.position ?? dkRec?.Position ??
        fromData.pos ??
        "";
      const posList = splitPos(rawPos);
      if (!posList.length) return null;

      let slateTag = slateInfo.slateTag;
      if (!slateTag && !siteOnly) slateTag = "main";
      if (!slateTag) return null;
      if (slate === "late" && !(slateTag === "late" || slateTag === "both")) return null;
      if (slate !== "late" && !(slateTag === "main" || slateTag === "both")) return null;

      const salProj = pickFirst(r, CFG.salFieldsProj);
      const salData = site === "dk" ? fromData.dkSal : fromData.fdSal;
      let salary = 0;
      if (isValidNbaSalary(slateInfo.salary)) salary = Number(slateInfo.salary);
      else if (isValidNbaSalary(salProj)) salary = Number(salProj);
      else if (isValidNbaSalary(salData)) salary = Number(salData);
      if (!isValidNbaSalary(salary)) return null;

      const projVal = siteOnly ? 0 : Number(pickFirst(r, CFG.projFields)) || 0;
      const team = normalizeTeamAbbr(
        pickFirst(r, ["Team", "team", "Tm", "TEAM"]) ?? rec?.team ?? rec?.Team ?? fromData.team
      );
      const opp = normalizeTeamAbbr(
        pickFirst(r, ["Opp", "opp", "OPP", "opponent"]) ?? rec?.opp ?? rec?.Opp ?? fromData.opp
      );
      const time = toHhMm(pickFirst(r, ["Time", "time", "Start Time", "Game Time"]) || fromData.time || "");
      const gameKey = team && opp ? [team, opp].sort().join("@") : "";

      let rawPown = 0;
      if (!siteOnly) {
        let usedKey = null;
        for (const k of CFG.pownFields) {
          if (r && r[k] != null) {
            usedKey = k;
            break;
          }
        }
        const rawVal = usedKey ? r[usedKey] : undefined;
        const hasPercentSignOrKey =
          (usedKey && usedKey.includes("%")) || /%/.test(String(rawVal ?? ""));
        const p = Number(String(rawVal ?? "").replace(/[ %]/g, ""));
        if (Number.isFinite(p)) rawPown = hasPercentSignOrKey ? p / 100 : p <= 1 ? p : p / 100;
      }

      const rawUsg = siteOnly ? 0 : pickFirst(r, CFG.usgFields) ?? fromData.usg ?? 0;
      const usgNum = numFromMaybePercent(rawUsg);
      const rawMin = siteOnly ? 0 : pickFirst(r, CFG.minFields) ?? fromData.minutes ?? 0;
      const minutesNum = Number(rawMin) || 0;
      const siteRef = getSiteId(nameRaw);
      const baseVal = salary > 0 ? (projVal / salary) * 1000 : 0;

      return {
        _idx: idx,
        key,
        name: nameRaw,
        posList,
        posDisp: posList.join("/"),
        team,
        opp,
        time,
        gameKey,
        salary,
        proj: projVal,
        originalProj: projVal,
        eproj: projVal,
        pown: rawPown,
        usg: usgNum,
        minutes: minutesNum,
        val: baseVal,
        baseVal,
        slate: slateTag,
        siteId: siteRef.id || slateInfo.id,
        slateCode: siteRef.slate,
        siteOnly,
        unprojected: siteOnly || projVal <= 0,
      };
    };

    projectedBase.forEach((r, idx) => {
      const nameRaw = r.Player ?? r.player ?? r.Name ?? r.name ?? "";
      const row = makeRow({ r, nameRaw, idx, siteOnly: false });
      if (row?.siteId) rowsByKey.set(row.key, row);
    });

    activeSiteRecords.forEach(({ name, rec }, idx) => {
      const key = aliasKey(name);
      if (rowsByKey.has(key)) return;
      const row = makeRow({ r: rec || {}, nameRaw: name, idx: projectedBase.length + idx, siteOnly: true });
      if (row?.siteId) rowsByKey.set(key, row);
    });

    return Array.from(rowsByKey.values()).sort((a, b) => {
      if ((a.siteOnly ? 1 : 0) !== (b.siteOnly ? 1 : 0)) return (a.siteOnly ? 1 : 0) - (b.siteOnly ? 1 : 0);
      return (b.proj || 0) - (a.proj || 0) || a.name.localeCompare(b.name);
    });
  }, [proj, CFG, site, slate, dataByName, siteIdMaps, customProj]);

  const rows = useMemo(() => {
    return baseRows.map((r) => {
      const b = Number(boost[r.name]) || 0;
      const customRaw = customProj[r.name];
      const hasCustom = customRaw !== undefined && customRaw !== null && String(customRaw).trim() !== "";
      const originalProj = Number(r.originalProj ?? r.proj ?? 0);
      const activeProj = hasCustom ? Math.max(0, Number(customRaw) || 0) : originalProj;
      const mult = 1 + b * 0.03 + (Number(teamBoost[r.team]) || 0) * 0.10;
      const eproj = activeProj * mult;
      const val = r.salary > 0 ? (eproj / r.salary) * 1000 : 0;
      return {
        ...r,
        originalProj,
        proj: activeProj,
        projBase: activeProj,
        customProj: customRaw ?? "",
        eproj,
        val,
        hasCustomProj: hasCustom,
      };
    });
  }, [baseRows, boost, teamBoost, customProj]);

const rowByAlias = useMemo(() => {
  const m = new Map();

  for (const r of rows) {
    const nm = String(r.name || "").trim();
    const siteId = String(r.siteId || "").trim();

    if (nm) {
      m.set(aliasKey(nm), r);
    }

    if (siteId) {
      m.set(`id:${siteId}`, r);
      const dashIdx = siteId.lastIndexOf("-");
      if (dashIdx >= 0) m.set(`id:${siteId.slice(dashIdx + 1)}`, r);
    }
  }

  return m;
}, [rows]);

  const games = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if (!r.gameKey) continue;
      const [a, b] = r.gameKey.split("@");
      if (!m.has(r.gameKey)) m.set(r.gameKey, { key: r.gameKey, a, b, time: r.time || "" });
    }
    return [...m.values()].sort((x, y) => String(x.time).localeCompare(String(y.time)));
  }, [rows]);

  const autoLockedGames = useMemo(() => {
    const s = new Set();
    if (!useAutoStarted) return s;
    for (const g of games) {
      if (sameDayHasStarted(g.time)) s.add(g.key);
    }
    return s;
  }, [games, useAutoStarted]);

  const effectiveLockedGames = useMemo(() => {
    const s = new Set(lockedGames);
    for (const g of autoLockedGames) s.add(g);
    return s;
  }, [lockedGames, autoLockedGames]);

  useEffect(() => {
    if (!uploadBundle) return;

    if (uploadBundle.kind === "contest") {
      const found =
        uploadBundle.contests.find((c) => c.name === selectedContestName) ||
        uploadBundle.contests[0] ||
        null;
      setImportedLineups(found?.lineups || []);
      if (found && found.name !== selectedContestName) {
        setSelectedContestName(found.name);
      }
    } else {
      setImportedLineups(uploadBundle.lineups || []);
      if (selectedContestName) setSelectedContestName("");
    }

    setSwappedLineups([]);
  }, [uploadBundle, selectedContestName, setImportedLineups, setSwappedLineups, setSelectedContestName]);

function hydrateLineup(lineup) {
  const assignment = (lineup.assignment || []).map((x) => {
    const rawPlayerId = String(x.playerId || "").trim();
    const rawSlateCode = String(x.slateCode || "").trim();
    const rawName = String(x.name || "").trim();
    const rawOriginalCell = String(x.originalCell || "").trim();

    const parsedOriginal = parseExportCell(site, rawOriginalCell);

const compositeId =
  rawSlateCode && rawPlayerId ? `${rawSlateCode}-${rawPlayerId}` : "";

const parsedCompositeId =
  parsedOriginal.slateCode && parsedOriginal.playerId
    ? `${parsedOriginal.slateCode}-${parsedOriginal.playerId}`
    : "";

const byCompositeId = compositeId ? rowByAlias.get(`id:${compositeId}`) : null;
const byParsedCompositeId = parsedCompositeId
  ? rowByAlias.get(`id:${parsedCompositeId}`)
  : null;

const byName = rawName ? rowByAlias.get(aliasKey(rawName)) : null;
const byParsedName = parsedOriginal.name
  ? rowByAlias.get(aliasKey(parsedOriginal.name))
  : null;

const row =
  byCompositeId ||
  byParsedCompositeId ||
  byName ||
  byParsedName ||
  null;

    const resolvedName =
      row?.name ||
      parsedOriginal.name ||
      rawName ||
      "";

    const resolvedPlayerId =
      rawPlayerId ||
      parsedOriginal.playerId ||
      row?.siteId ||
      "";

    const resolvedSlateCode =
      rawSlateCode ||
      parsedOriginal.slateCode ||
      row?.slateCode ||
      "";

    const lockedByGame = row?.gameKey ? effectiveLockedGames.has(row.gameKey) : false;
    const locked = !!parsedOriginal.lockedTag || lockedByGame;

    return {
      ...x,
      name: resolvedName,
      playerId: resolvedPlayerId,
      slateCode: resolvedSlateCode,
      row,
      gameKey: row?.gameKey || "",
      team: row?.team || "",
      opp: row?.opp || "",
      time: row?.time || "",
      salary: row?.salary || 0,
      proj: row?.eproj || 0,
      pown: row?.pown || 0,
      usg: row?.usg || 0,
      minutes: row?.minutes || 0,
      posDisp: row?.posDisp || "",
      locked,
    };
  });

  const lockedAssignment = assignment
    .filter((x) => x.locked && x.name)
    .map((x) => ({ slot: x.slot, name: x.name }));

  const openSlots = CFG.slots.filter(
    (s) => !lockedAssignment.some((x) => x.slot === s.name)
  );

  const lockedSalary = assignment
    .filter((x) => x.locked)
    .reduce((sum, x) => sum + (Number(x.salary) || 0), 0);

  const remainingSalary = CFG.cap - lockedSalary;
  const usedLocked = new Set(lockedAssignment.map((x) => x.name));

  const eligiblePlayers = rows.filter((r) => {
    if (!r.siteId) return false;
    if (usedLocked.has(r.name)) return false;
    if (r.gameKey && effectiveLockedGames.has(r.gameKey)) return false;
    return true;
  });

  return {
    ...lineup,
    assignment,
    lockedAssignment,
    openSlots,
    lockedSalary,
    remainingSalary,
    eligiblePlayers,
  };
}

  const hydratedImported = useMemo(
    () => importedLineups.map((x) => hydrateLineup(x)),
    [importedLineups, rows, effectiveLockedGames, site]
  );

    const lineupUsageByName = useMemo(() => {
    const counts = new Map();
    const source = swappedLineups.length ? swappedLineups : hydratedImported;

    for (const lineup of source) {
      const arr = swappedLineups.length
        ? (lineup.finalAssignment || [])
        : (lineup.assignment || []);

      for (const a of arr) {
        const nm = String(a?.name || "").trim();
        if (!nm || nm === "—") continue;
        counts.set(nm, (counts.get(nm) || 0) + 1);
      }
    }

    return counts;
  }, [swappedLineups, hydratedImported]);

  const lineupUsagePctByName = useMemo(() => {
    const pct = new Map();
    const total = Math.max(swappedLineups.length || hydratedImported.length || 0, 1);

    for (const [name, count] of lineupUsageByName.entries()) {
      pct.set(name, (count / total) * 100);
    }

    return pct;
  }, [lineupUsageByName, swappedLineups.length, hydratedImported.length]);
  
  const availablePool = useMemo(() => {
    const lockedNamesFromCSV = new Set();
    for (const lineup of hydratedImported) {
      for (const a of lineup.lockedAssignment || []) {
        if (a.name) lockedNamesFromCSV.add(a.name);
      }
    }

    const txt = String(q || "").trim().toLowerCase();

    let arr = rows
      .filter((r) => {
        if (!r.siteId) return false;
        if (r.siteOnly && !showAllPlayers) return false;
        if (r.gameKey && effectiveLockedGames.has(r.gameKey)) return false;
        if (lockedNamesFromCSV.has(r.name)) return false;
        if (!positionMatchesFilter(r.posList || [], posFilter)) return false;

        if (!txt) return true;

        const salaryText = String(r.salary || "");
        return (
          r.name.toLowerCase().includes(txt) ||
          String(r.team || "").toLowerCase().includes(txt) ||
          String(r.opp || "").toLowerCase().includes(txt) ||
          String(r.posDisp || "").toLowerCase().includes(txt) ||
          salaryText.includes(txt)
        );
      })
      .map((r) => ({
        ...r,
        usedPct: lineupUsagePctByName.get(r.name) || 0,
      }));

    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      arr = arr.slice().sort((a, b) => {
        let av = a[sort.col];
        let bv = b[sort.col];

        if (sort.col === "eproj") {
          // Keep row order stable when custom projections / boosts change.
          // Sort by the original projection while displaying the edited/boosted number.
          av = a.originalProj || 0;
          bv = b.originalProj || 0;
        }
        if (sort.col === "val") {
          av = a.baseVal || 0;
          bv = b.baseVal || 0;
        }
        if (sort.col === "pown") {
          av = a.pown || 0;
          bv = b.pown || 0;
        }
        if (sort.col === "usedPct") {
          av = a.usedPct || 0;
          bv = b.usedPct || 0;
        }
        if (sort.col === "minutes") {
          av = a.minutes || 0;
          bv = b.minutes || 0;
        }

        if (typeof av === "string") av = av.toLowerCase();
        if (typeof bv === "string") bv = bv.toLowerCase();

        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }

    return arr;
  }, [rows, hydratedImported, effectiveLockedGames, posFilter, q, sort, lineupUsagePctByName, showAllPlayers]);

  async function onUploadFile(file) {
    if (!file) return;
    const text = await file.text();
    setCsvName(file.name || "");
    setParseError("");
    setStatusMsg("");
    try {
      const parsed = parseUploadedCsv(text, site);
      setUploadBundle(parsed);
      setSelectedContestName(parsed.selectedContestName || "");
      setSwappedLineups([]);
      setImportedLineups(parsed.lineups || []);
      setStatusMsg(
        parsed.kind === "contest"
          ? `Contest CSV loaded. ${parsed.contests.length} contest(s) found.`
          : `Lineups CSV loaded. ${parsed.lineups.length} lineup(s) found.`
      );
    } catch (e) {
      setUploadBundle(null);
      setImportedLineups([]);
      setSwappedLineups([]);
      setParseError(e?.message || "Could not parse uploaded CSV.");
    }
  }

  function clearUpload() {
    setCsvName("");
    setUploadBundle(null);
    setImportedLineups([]);
    setSwappedLineups([]);
    setParseError("");
    setStatusMsg("");
    setSelectedContestName("");
    if (fileRef.current) fileRef.current.value = "";
  }

  function resetRules() {
    setLocks(new Set());
    setExcls(new Set());
    setBoost({});
    setTeamBoost({});
    setProjDraft({});
    setCustomProj({});
    setUnprojectedAllows(new Set());
    setShowAllPlayers(false);
    setMinExp({});
    setMaxExp({});
    setRandomness(0);
    setMaxFromTeam("");
    setLineupPownMax("");
    setMaxPlayerPown("");
    setPosFilter("ALL");
    setQ("");
  }

  function buildExportRows() {
    const order = canonicalSlotOrder(site);

    if (!swappedLineups.length) return null;

    if (!uploadBundle || uploadBundle.kind === "lineups") {
      const rowsOut = [CFG.exportHeader];

      for (const lineup of swappedLineups) {
        const bySlot = {};
        for (const a of lineup.finalAssignment || []) bySlot[a.slot] = a;

        const row = order.map((slot) => {
          const item = bySlot[slot];
          if (!item?.name) return "";
          return formatExportCell(site, {
            name: item.name,
            playerId: item.playerId || "",
            slateCode: item.slateCode || "",
          });
        });

        rowsOut.push(row);
      }

      return rowsOut;
    }

    const outRows = (uploadBundle.rawRows || []).map((r) => [...r]);
    const slotIndexes = uploadBundle.slotIndexes || [];

    for (let i = 0; i < swappedLineups.length; i++) {
      const lineup = swappedLineups[i];
      const rowIdx = lineup.sourceRowIndex;
      if (rowIdx == null || !outRows[rowIdx]) continue;

      const bySlot = {};
      for (const a of lineup.finalAssignment || []) bySlot[a.slot] = a;

      order.forEach((slot, j) => {
        const cellIdx = slotIndexes[j];
        const item = bySlot[slot];
        outRows[rowIdx][cellIdx] = item?.name
          ? formatExportCell(site, {
              name: item.name,
              playerId: item.playerId || getSiteId(item.name).id || "",
              slateCode: item.slateCode || getSiteId(item.name).slate || "",
            })
          : "";
      });
    }

    return outRows;
  }

  function exportSwappedCsv() {
    const rowsOut = buildExportRows();
    if (!rowsOut?.length) return;

    const csv = toCsv(rowsOut);
    const baseName = csvName
      ? csvName.replace(/\.csv$/i, "")
      : `nba_${site}_late_swap_${new Date().toISOString().slice(0, 10)}`;

    const suffix =
      uploadBundle?.kind === "contest" && selectedContestName
        ? `_${selectedContestName.replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "")}_swapped`
        : "_swapped";

    download(`${baseName}${suffix}.csv`, csv);
  }

  async function runLateSwap() {
    if (!hydratedImported.length) return;

    setRunning(true);
    setStatusMsg("Running backend late swap...");
    setSwappedLineups([]);

    try {
      const out = [];
      const totalLineups = hydratedImported.length;
      const exposureCounts = new Map();

      const getMaxCount = (name) => {
        const raw = normalizePctValue(maxExp[name]);
        if (raw === "") return null;
        return Math.floor((raw / 100) * totalLineups + 1e-9);
      };

      const getMinCount = (name) => {
        const raw = normalizePctValue(minExp[name]);
        if (raw === "") return null;
        return Math.ceil((raw / 100) * totalLineups - 1e-9);
      };

      for (let i = 0; i < hydratedImported.length; i++) {
        const lineup = hydratedImported[i];
        setStatusMsg(`Running backend late swap... ${i + 1}/${hydratedImported.length}`);

        const originalOrdered = canonicalSlotOrder(site).map((slot) => {
          const found = lineup.assignment.find((x) => x.slot === slot);
          return { slot, name: found?.name || "" };
        });

        if (!lineup.openSlots.length) {
          const finalHydrated = originalOrdered.map((x) => {
            const row = x.name ? rowByAlias.get(aliasKey(x.name)) : null;
            const originalItem = lineup.assignment.find((a) => a.slot === x.slot);
            const locked = !!lineup.lockedAssignment.find((k) => k.slot === x.slot && k.name === x.name);
            return {
              ...x,
              locked,
              row,
              playerId: originalItem?.playerId || row?.siteId || "",
              slateCode: originalItem?.slateCode || row?.slateCode || "",
              salary: row?.salary || 0,
              proj: row?.eproj || 0,
              pown: row?.pown || 0,
              usg: row?.usg || 0,
              minutes: row?.minutes || 0,
              team: row?.team || "",
              posDisp: row?.posDisp || "",
            };
          });

          for (const a of finalHydrated) {
            if (a.name) exposureCounts.set(a.name, (exposureCounts.get(a.name) || 0) + 1);
          }

          out.push({
            ...lineup,
            finalAssignment: finalHydrated,
            finalSalary: finalHydrated.reduce((s, x) => s + (x.salary || 0), 0),
            finalProj: finalHydrated.reduce((s, x) => s + (x.proj || 0), 0),
            finalPown: finalHydrated.reduce((s, x) => s + ((x.pown || 0) * 100), 0),
            finalAvgUsg: finalHydrated.length
              ? finalHydrated.reduce((s, x) => s + (x.usg || 0), 0) / finalHydrated.length
              : 0,
            changed: false,
            backendError: "",
          });
          continue;
        }

        try {
          const dynamicExcludes = new Set(excls);
          for (const r of rows) {
            if (r.siteOnly && !unprojectedAllows.has(r.name)) dynamicExcludes.add(r.name);
          }
          const dynamicLocks = new Set(locks);
          const dynamicBoosts = { ...boost };

          const remainingIncludingCurrent = totalLineups - i;

          for (const r of rows) {
            const maxCt = getMaxCount(r.name);
            if (maxCt != null) {
              const used = exposureCounts.get(r.name) || 0;
              if (used >= maxCt) dynamicExcludes.add(r.name);
            }
          }

          for (const r of rows) {
            const minCt = getMinCount(r.name);
            if (minCt == null || minCt <= 0) continue;

            const used = exposureCounts.get(r.name) || 0;
            const need = minCt - used;
            if (need <= 0) continue;
            if (dynamicExcludes.has(r.name)) continue;

            const mustNow = need >= remainingIncludingCurrent;
            if (mustNow) {
              dynamicLocks.add(r.name);
            } else {
              const urgency = need / remainingIncludingCurrent;
              const extra = Math.max(1, Math.min(9, Math.ceil(urgency * 9)));
              dynamicBoosts[r.name] = clamp((dynamicBoosts[r.name] || 0) + extra, -9, 9);
            }
          }

          const lockedNamesInLineup = new Set(lineup.lockedAssignment.map((x) => x.name));
          const eligiblePlayers = rows.filter((r) => {
            if (!r.siteId) return false;
            if (lockedNamesInLineup.has(r.name)) return false;
            if (r.gameKey && effectiveLockedGames.has(r.gameKey)) return false;
            if (dynamicExcludes.has(r.name)) return false;
            return true;
          });

          const payload = buildLateSwapPayload({
            site,
            lineup,
            openSlots: lineup.openSlots,
            lockedAssignment: lineup.lockedAssignment,
            eligiblePlayers,
            remainingSalary: lineup.remainingSalary,
            boosts: dynamicBoosts,
            randomness,
            maxFromTeam,
            lineupPownMax,
            maxPlayerPown,
            excludes: dynamicExcludes,
            locks: dynamicLocks,
          });

          const result = await runLateSwapBackend(payload);
          const normalized = normalizeBackendAssignment(site, result.assignment || []);
          const changed = JSON.stringify(originalOrdered) !== JSON.stringify(normalized);

          const finalHydrated = normalized.map((x) => {
            const row = x.name ? rowByAlias.get(aliasKey(x.name)) : null;
            const originalItem = lineup.assignment.find((a) => a.slot === x.slot);
            const samePlayer =
              aliasKey(originalItem?.name || "") === aliasKey(x.name || "");
            const locked = !!lineup.lockedAssignment.find(
              (k) => k.slot === x.slot && k.name === x.name
            );

            let exportPlayerId = "";
            let exportSlateCode = "";

            if (samePlayer) {
              exportPlayerId = originalItem?.playerId || "";
              exportSlateCode = originalItem?.slateCode || "";
            } else {
              const siteId = String(row?.siteId || "").trim();
              const dashIdx = siteId.lastIndexOf("-");

              if (dashIdx >= 0) {
                exportSlateCode = siteId.slice(0, dashIdx).trim();
                exportPlayerId = siteId.slice(dashIdx + 1).trim();
              } else {
                exportPlayerId = siteId;
                exportSlateCode = String(row?.slateCode || "").trim();
              }
            }

            return {
              ...x,
              locked,
              row,
              playerId: exportPlayerId,
              slateCode: exportSlateCode,
              salary: row?.salary || 0,
              proj: row?.eproj || 0,
              pown: row?.pown || 0,
              usg: row?.usg || 0,
              minutes: row?.minutes || 0,
              team: row?.team || "",
              posDisp: row?.posDisp || "",
            };
          });

          for (const a of finalHydrated) {
            if (a.name) exposureCounts.set(a.name, (exposureCounts.get(a.name) || 0) + 1);
          }

          out.push({
            ...lineup,
            finalAssignment: finalHydrated,
            finalSalary: finalHydrated.reduce((s, x) => s + (x.salary || 0), 0),
            finalProj: finalHydrated.reduce((s, x) => s + (x.proj || 0), 0),
            finalPown: finalHydrated.reduce((s, x) => s + ((x.pown || 0) * 100), 0),
            finalAvgUsg: finalHydrated.length
              ? finalHydrated.reduce((s, x) => s + (x.usg || 0), 0) / finalHydrated.length
              : 0,
            changed,
            backendError: "",
          });
        } catch (err) {
            const fallbackHydrated = originalOrdered.map((x) => {
              const row = x.name ? rowByAlias.get(aliasKey(x.name)) : null;
              const originalItem = lineup.assignment.find((a) => a.slot === x.slot);
              const samePlayer =
                aliasKey(originalItem?.name || "") === aliasKey(x.name || "");
              const locked = !!lineup.lockedAssignment.find(
                (k) => k.slot === x.slot && k.name === x.name
              );

              let exportPlayerId = "";
              let exportSlateCode = "";

              if (samePlayer) {
                exportPlayerId = originalItem?.playerId || "";
                exportSlateCode = originalItem?.slateCode || "";
              } else {
                const siteId = String(row?.siteId || "").trim();
                const dashIdx = siteId.lastIndexOf("-");

                if (dashIdx >= 0) {
                  exportSlateCode = siteId.slice(0, dashIdx).trim();
                  exportPlayerId = siteId.slice(dashIdx + 1).trim();
                } else {
                  exportPlayerId = siteId;
                  exportSlateCode = String(row?.slateCode || "").trim();
                }
              }

              return {
                ...x,
                locked,
                row,
                playerId: exportPlayerId,
                slateCode: exportSlateCode,
                salary: row?.salary || 0,
                proj: row?.eproj || 0,
                pown: row?.pown || 0,
                usg: row?.usg || 0,
                minutes: row?.minutes || 0,
                team: row?.team || "",
                posDisp: row?.posDisp || "",
              };
            });

          for (const a of fallbackHydrated) {
            if (a.name) exposureCounts.set(a.name, (exposureCounts.get(a.name) || 0) + 1);
          }

          out.push({
            ...lineup,
            finalAssignment: fallbackHydrated,
            finalSalary: fallbackHydrated.reduce((s, x) => s + (x.salary || 0), 0),
            finalProj: fallbackHydrated.reduce((s, x) => s + (x.proj || 0), 0),
            finalPown: fallbackHydrated.reduce((s, x) => s + ((x.pown || 0) * 100), 0),
            finalAvgUsg: fallbackHydrated.length
              ? fallbackHydrated.reduce((s, x) => s + (x.usg || 0), 0) / fallbackHydrated.length
              : 0,
            changed: false,
            backendError: err?.message || "Late swap failed",
          });
        }
      }

      setSwappedLineups(out);
      const changedCt = out.filter((x) => x.changed).length;
      const errorCt = out.filter((x) => x.backendError).length;
      setStatusMsg(
        `Finished. ${changedCt} of ${out.length} lineups changed.${errorCt ? ` ${errorCt} lineup(s) failed.` : ""}`
      );

      const usage = buildUsageTables(out);
      const saved = {
        id: `${Date.now()}`,
        createdAt: new Date().toISOString(),
        site,
        slate,
        csvName,
        uploadKind: uploadBundle?.kind || "lineups",
        selectedContestName: uploadBundle?.kind === "contest" ? selectedContestName : "",
        lineupCount: out.length,
        changedCount: changedCt,
        errorCount: errorCt,
        results: out,
        usage,
      };

      setSavedBuilds((prev) => [saved, ...(prev || [])].slice(0, MAX_SAVED_BUILDS));
    } finally {
      setRunning(false);
    }
  }

  const visibleSwapped = useMemo(() => {
    const base = swappedLineups.length
      ? swappedLineups
      : hydratedImported.map((x) => ({
          ...x,
          finalAssignment: x.assignment.map((a) => ({
            slot: a.slot,
            name: a.name,
            locked: a.locked,
            row: a.row,
            playerId: a.playerId || a.row?.siteId || "",
            slateCode: a.slateCode || a.row?.slateCode || "",
            salary: a.salary,
            proj: a.proj,
            pown: a.pown,
            usg: a.usg,
            minutes: a.minutes,
            team: a.team,
            posDisp: a.posDisp,
          })),
          finalSalary: x.assignment.reduce((s, a) => s + (a.salary || 0), 0),
          finalProj: x.assignment.reduce((s, a) => s + (a.proj || 0), 0),
          finalPown: x.assignment.reduce((s, a) => s + ((a.pown || 0) * 100), 0),
          finalAvgUsg: x.assignment.length
            ? x.assignment.reduce((s, a) => s + (a.usg || 0), 0) / x.assignment.length
            : 0,
          changed: false,
          backendError: "",
        }));

    return showOnlyChanged ? base.filter((x) => x.changed) : base;
  }, [swappedLineups, hydratedImported, showOnlyChanged]);

  const usageSource = useMemo(
    () => (swappedLineups.length ? swappedLineups : hydratedImported),
    [swappedLineups, hydratedImported]
  );

  const usageTables = useMemo(() => buildUsageTables(usageSource), [usageSource]);
  const playerUsageRows = usageTables.players;
  const teamUsageRows = usageTables.teams;

  const toggleSort = (col) =>
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" }
    );

const th =
  "px-2 py-1 text-[11px] font-semibold text-center select-none whitespace-nowrap align-middle";
const td =
  "px-2 py-0.5 text-xs text-center whitespace-nowrap align-middle";

  const contestOptions = uploadBundle?.kind === "contest" ? uploadBundle.contests || [] : [];
  const currentContestCount =
    uploadBundle?.kind === "contest"
      ? (contestOptions.find((c) => c.name === selectedContestName)?.count || 0)
      : 0;

  const visiblePool = availablePool.filter((r) => !hideExcluded || !(excls.has(r.name) || (r.siteOnly && !unprojectedAllows.has(r.name))));

  return (
    <div className={`nba-late-swap ${isDark ? "theme-dark" : "theme-light"} px-4 md:px-6 py-5`}>
      <style>{`
        .nba-late-swap.theme-dark{
          background:#020617;
          min-height:100vh;
          color: #e5e7eb;
        }
        .nba-late-swap.theme-light{ background:#f1f5f9; min-height:100vh; color:#0f172a; }
        .nba-late-swap.theme-dark h1,
        .nba-late-swap.theme-dark h2,
        .nba-late-swap.theme-dark h3,
        .nba-late-swap.theme-dark h4{ color:#f8fafc; }

        /* Custom projection colors need !important because NBA late-swap theme input rules also use !important. */
        .nba-late-swap input.nba-proj-up{
          color:#047857 !important;
          -webkit-text-fill-color:#047857 !important;
          border-color:#10b981 !important;
          background-color:#ecfdf5 !important;
          font-weight:700 !important;
        }
        .nba-late-swap.theme-dark input.nba-proj-up{
          color:#a7f3d0 !important;
          -webkit-text-fill-color:#a7f3d0 !important;
          border-color:#34d399 !important;
          background-color:rgba(16,185,129,0.18) !important;
        }
        .nba-late-swap input.nba-proj-down{
          color:#b91c1c !important;
          -webkit-text-fill-color:#b91c1c !important;
          border-color:#ef4444 !important;
          background-color:#fef2f2 !important;
          font-weight:700 !important;
        }
        .nba-late-swap.theme-dark input.nba-proj-down{
          color:#fecaca !important;
          -webkit-text-fill-color:#fecaca !important;
          border-color:#f87171 !important;
          background-color:rgba(239,68,68,0.18) !important;
        }

        .nba-late-swap.theme-dark .bg-white{ background-color: rgba(15, 23, 42, 0.72) !important; }
        .nba-late-swap.theme-dark .bg-gray-50{ background-color: rgba(2, 6, 23, 0.55) !important; }
        .nba-late-swap.theme-dark .bg-gray-100{ background-color: rgba(2, 6, 23, 0.72) !important; }
        .nba-late-swap.theme-dark .text-gray-900{ color:#f8fafc !important; }
        .nba-late-swap.theme-dark .text-gray-800{ color:#f1f5f9 !important; }
        .nba-late-swap.theme-dark .text-gray-700{ color:#e2e8f0 !important; }
        .nba-late-swap.theme-dark .text-gray-600{ color:#cbd5e1 !important; }
        .nba-late-swap.theme-dark .text-gray-500{ color:#94a3b8 !important; }
        .nba-late-swap.theme-dark .border-gray-100{ border-color: rgba(148,163,184,0.18) !important; }
        .nba-late-swap.theme-dark .border-gray-200{ border-color: rgba(148,163,184,0.18) !important; }
        .nba-late-swap.theme-dark .border-gray-300{ border-color: rgba(148,163,184,0.24) !important; }
        .nba-late-swap.theme-dark .shadow,
        .nba-late-swap.theme-dark .shadow-sm,
        .nba-late-swap.theme-dark .shadow-md{
          box-shadow: 0 12px 30px rgba(0,0,0,0.35) !important;
        }
        .nba-late-swap.theme-dark input,
        .nba-late-swap.theme-dark select,
        .nba-late-swap.theme-dark textarea{
          background-color: rgba(2, 6, 23, 0.45) !important;
          border-color: rgba(148,163,184,0.22) !important;
          color:#e5e7eb !important;
        }
        .nba-late-swap.theme-dark input::placeholder,
        .nba-late-swap.theme-dark textarea::placeholder{
          color: rgba(148,163,184,0.70) !important;
        }
        .nba-late-swap.theme-dark .hover\\:bg-gray-50:hover{ background-color: rgba(30, 41, 59, 0.55) !important; }
        .nba-late-swap.theme-dark .hover\\:bg-gray-100:hover{ background-color: rgba(30, 41, 59, 0.70) !important; }
        .nba-late-swap.theme-dark .bg-blue-600{ background-color: rgba(59,130,246,0.90) !important; }
        .nba-late-swap.theme-dark .bg-blue-50{ background-color: rgba(59,130,246,0.14) !important; }
        .nba-late-swap.theme-dark .text-blue-600{ color: #93c5fd !important; }
        .nba-late-swap.theme-dark .bg-red-50{ background-color: rgba(239,68,68,0.10) !important; }
        .nba-late-swap.theme-dark thead{ color: rgba(203,213,225,0.95) !important; }
        .nba-late-swap.theme-dark th{ border-color: rgba(148,163,184,0.18) !important; }
        .nba-late-swap.theme-dark tr{ border-color: rgba(148,163,184,0.18) !important; }
        .nba-late-swap.theme-dark .late-table-head th,
        .nba-late-swap.theme-dark .late-table-head{
          background-color:#070b1a !important;
          color:#e5e7eb !important;
        }
        .nba-late-swap.theme-light .late-table-head th,
        .nba-late-swap.theme-light .late-table-head{
          background-color:#f8fafc !important;
          color:#0f172a !important;
        }
        .nba-late-swap.theme-dark .late-zebra-even{ background-color:rgba(2,6,23,0.58) !important; }
        .nba-late-swap.theme-dark .late-zebra-odd{ background-color:rgba(15,23,42,0.78) !important; }
        .nba-late-swap.theme-light .late-zebra-even{ background-color:#ffffff !important; }
        .nba-late-swap.theme-light .late-zebra-odd{ background-color:#f8fafc !important; }
        .nba-late-swap .late-zebra-row:hover{ background-color:rgba(30,64,175,0.18) !important; }
        .nba-late-swap.theme-dark .row-excluded{ background-color:rgba(15,23,42,0.55) !important; opacity:0.82; }
        .nba-late-swap.theme-light .row-excluded{ background-color:#f1f5f9 !important; opacity:0.86; }
      `}</style>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3 lg:flex-nowrap">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="whitespace-nowrap text-2xl md:text-3xl font-extrabold">NBA — Late Swap</h1>
          <div className="flex flex-wrap items-baseline gap-x-3 text-xs text-slate-500">
            <span className="italic">Updated: {fmtUpdated(updatedAt)}</span>
            <span>{rows.length} rows</span>
          </div>
        </div>

        <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto sm:shrink-0">
        <div className="inline-flex items-center rounded-full border border-gray-300 bg-white p-1">
          <button
            type="button"
            className={
              "px-3 py-1 text-[11px] font-medium rounded-full " +
              (isDark ? "bg-blue-600 text-white" : "text-gray-700")
            }
            onClick={() => setTheme("dark")}
          >
            Dark
          </button>
          <button
            type="button"
            className={
              "px-3 py-1 text-[11px] font-medium rounded-full " +
              (!isDark ? "bg-blue-600 text-white" : "text-gray-500")
            }
            onClick={() => setTheme("light")}
          >
            Light
          </button>
        </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs text-slate-500">Click here if new slate →</span>
            <button type="button" onClick={resetRules} className="h-8 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700">Reset</button>
          </div>
        </div>
      </div>

      <section aria-label="Slate and site" className={panelClass + " mb-3 p-3"}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:flex-nowrap">
            <div
              className={tabWrapClass}
            >
              <button
                type="button"
                onClick={() => setActiveTab("optimizer")}
                className={navTabClass(activeTab === "optimizer")}
              >
                Optimizer
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("lateSwap")}
                className={navTabClass(activeTab === "lateSwap")}
              >
                Late Swap
              </button>
            </div>

            <div className={tabWrapClass}>
            {["dk", "fd"].map((s) => (
              <button
                key={s}
                onClick={() => {
                  setSite(s);
                  setUploadBundle(null);
                  setImportedLineups([]);
                  setSwappedLineups([]);
                  setParseError("");
                  setCsvName("");
                  setStatusMsg("");
                  setSelectedContestName("");
                  setLocks(new Set());
                  setExcls(new Set());
                  setBoost({});
                  setTeamBoost({});
                  setProjDraft({});
                  setMinExp({});
                  setMaxExp({});
                  if (fileRef.current) fileRef.current.value = "";
                }}
                className={navTabClass(site === s)}
              >
                <img
                  src={SITES[s].logo}
                  alt=""
                  className="w-4 h-4 object-contain"
                />
                <span className="font-medium">{SITES[s].label}</span>
              </button>
            ))}
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <span className="flex h-9 items-center pb-2 text-xs text-slate-500">Slate:</span>
            <div className={tabWrapClass}>
            {[
              { key: "main", label: "Main" },
              { key: "late", label: "Late" },
            ].map((sl) => (
              <button
                key={sl.key}
                onClick={() => {
                  setSlate(sl.key);
                  setTeamBoost({});
                  setSwappedLineups([]);
                }}
                className={navTabClass(slate === sl.key)}
              >
                {sl.label}
              </button>
            ))}
            </div>
          </div>

        </div>
      </section>
      <section aria-label="Late swap actions" className={panelClass + " mb-3 p-3"}>
        <div className="flex items-center gap-2 flex-wrap">
          <label
            className={
              "h-9 px-3 rounded-md border text-sm inline-flex items-center cursor-pointer transition " +
              (isDark
                ? "border-gray-300 bg-white hover:bg-gray-50 text-gray-900"
                : "border-gray-300 bg-white hover:bg-gray-100 text-gray-900")
            }
          >
            Upload CSV
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => onUploadFile(e.target.files?.[0])}
            />
          </label>

          <button
            className={
              "h-9 px-3 rounded-md border text-sm transition " +
              (isDark
                ? "border-gray-300 bg-white hover:bg-gray-50 text-gray-900"
                : "border-gray-300 bg-white hover:bg-gray-100 text-gray-900")
            }
            onClick={clearUpload}
            disabled={!importedLineups.length && !csvName}
          >
            Clear
          </button>

          <button
            className="h-9 px-3 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-sm disabled:opacity-60"
            onClick={runLateSwap}
            disabled={!importedLineups.length || running}
          >
            {running ? "Running…" : "Run Late Swap"}
          </button>

          <button
            className={
              "h-9 px-3 rounded-md border text-sm transition disabled:opacity-60 " +
              (isDark
                ? "border-gray-300 bg-white hover:bg-gray-50 text-gray-900"
                : "border-gray-300 bg-white hover:bg-gray-100 text-gray-900")
            }
            onClick={exportSwappedCsv}
            disabled={!swappedLineups.length}
          >
            Export Swapped CSV
          </button>
        </div>
        <details className="mt-3 border-t border-slate-500/20 pt-3">
          <summary className="cursor-pointer select-none text-sm font-semibold">Reset Tools</summary>
          <div className="mt-3">
      <div className="flex flex-wrap gap-2">
        <button
          className="px-3 py-1 rounded-md border bg-white hover:bg-gray-50 text-xs"
          onClick={() => setExcls(new Set())}
        >
          Clear Excludes
        </button>

        <button
          className="px-3 py-1 rounded-md border bg-white hover:bg-gray-50 text-xs"
          onClick={() => setLocks(new Set())}
        >
          Clear Locks
        </button>

        <button className="h-8 px-3 rounded-md border bg-white hover:bg-gray-50 text-xs" onClick={resetRules}>
          Reset All Rules
        </button>
        <button className="h-8 px-3 rounded-md border bg-white hover:bg-gray-50 text-xs" onClick={() => { setBoost({}); setTeamBoost({}); }}>
          Reset Boosts
        </button>
        <button className="h-8 px-3 rounded-md border bg-white hover:bg-gray-50 text-xs" onClick={() => setLocks(new Set())}>
          Reset Locks
        </button>
        <button className="h-8 px-3 rounded-md border bg-white hover:bg-gray-50 text-xs" onClick={() => setExcls(new Set())}>
          Reset Excludes
        </button>
        <button className="h-8 px-3 rounded-md border bg-white hover:bg-gray-50 text-xs" onClick={() => { setMinExp({}); setMaxExp({}); }}>
          Reset Exposures
        </button>
        <button
          className="h-8 px-3 rounded-md border bg-white hover:bg-gray-50 text-xs"
          onClick={() => {
            setRandomness(0);
            setMaxFromTeam("");
            setLineupPownMax("");
            setMaxPlayerPown("");
          }}
        >
          Reset Constraints
        </button>
        <button
          className="h-8 px-3 rounded-md border bg-white hover:bg-gray-50 text-xs"
          onClick={() => {
            setPosFilter("ALL");
            setQ("");
          }}
        >
          Reset Filters
        </button>
      </div>

          </div>
        </details>
      </section>

      {uploadBundle?.kind === "contest" && (
        <div className="mb-4 rounded-2xl border border-gray-200 bg-white shadow-sm p-3">
          <div className="text-sm font-semibold mb-2">Contest Selection</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-[10px] text-gray-600 mb-1">Contest</label>
              <select
                className="h-9 px-2 rounded-md border border-gray-200 w-full text-sm"
                value={selectedContestName}
                onChange={(e) => {
                  setSelectedContestName(e.target.value);
                  setSwappedLineups([]);
                }}
              >
                {contestOptions.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.count})
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm text-gray-600">
              <div>Contest lines: <span className="font-semibold">{currentContestCount}</span></div>
              <div>Total contests in file: <span className="font-semibold">{contestOptions.length}</span></div>
            </div>

            <div className="text-sm text-gray-600">
              Export will preserve every other contest and only replace the selected contest’s lineup cells.
            </div>
          </div>
        </div>
      )}

      <section aria-label="Late swap settings" className={panelClass + " mb-3 p-3"}>
        <h2 className="mb-3 text-sm font-semibold">Late Swap Settings</h2>
      <div className="mb-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
        <div className="md:col-span-3">
          <label className="block text-[10px] text-gray-600 mb-1">Randomness %</label>
          <input
            className="h-8 px-2 text-xs rounded-md border border-gray-200 w-full"
            value={randomness}
            onChange={(e) => setRandomness(e.target.value)}
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-[10px] text-gray-600 mb-1">Max from team</label>
          <input
            className="h-8 px-2 text-xs rounded-md border border-gray-200 w-full"
            value={maxFromTeam}
            onChange={(e) => setMaxFromTeam(e.target.value)}
            placeholder="—"
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-[10px] text-gray-600 mb-1">Max lineup pOWN% (sum)</label>
          <input
            className="h-8 px-2 text-xs rounded-md border border-gray-200 w-full"
            value={lineupPownMax}
            onChange={(e) => setLineupPownMax(e.target.value)}
            placeholder="—"
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-[10px] text-gray-600 mb-1">Max player pOWN%</label>
          <input
            className="h-8 px-2 text-xs rounded-md border border-gray-200 w-full"
            value={maxPlayerPown}
            onChange={(e) => setMaxPlayerPown(e.target.value)}
            placeholder="—"
          />
        </div>
      </div>

      </section>

      <div className="mb-3 rounded-2xl border border-gray-200 bg-white shadow-sm p-3">
        <div className="text-sm font-semibold mb-2">Upload / Status</div>
        <div className="text-xs text-gray-600 mb-1">File: {csvName || "—"}</div>
        <div className="text-xs text-gray-600 mb-1">
          CSV Type: {uploadBundle?.kind ? uploadBundle.kind.toUpperCase() : "—"}
        </div>
        <div className="text-xs text-gray-600 mb-1">Imported lineups: {importedLineups.length}</div>
        <div className="text-xs text-gray-600 mb-1">Current site: {CFG.label}</div>
        <div className="text-xs text-gray-600 mb-1">Current slate: {slate.toUpperCase()}</div>
        {uploadBundle?.kind === "contest" && (
          <div className="text-xs text-gray-600 mb-1">Selected contest: {selectedContestName || "—"}</div>
        )}
        <div className="text-xs text-gray-600 mb-2">Effective locked games: {effectiveLockedGames.size}</div>

        {statusMsg && (
          <div className="text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
            {statusMsg}
          </div>
        )}
        {parseError && (
          <div className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 mt-2">
            {parseError}
          </div>
        )}
      </div>

      <div role="region" aria-label="Games" className={panelClass + " mb-4 p-3"}>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="text-sm font-semibold">Games</div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={useAutoStarted}
                onChange={(e) => setUseAutoStarted(e.target.checked)}
              />
              Auto-lock started games
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={showOnlyChanged}
                onChange={(e) => setShowOnlyChanged(e.target.checked)}
              />
              Show only changed
            </label>
            <button
              className="h-8 px-3 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-xs"
              onClick={() => setLockedGames(new Set(games.map((g) => g.key)))}
            >
              Lock All Games
            </button>
            <button
              className="h-8 px-3 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-xs"
              onClick={() => setLockedGames(new Set())}
            >
              Clear Manual Locks
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-2">
          {games.map((g) => {
            const manual = lockedGames.has(g.key);
            const auto = autoLockedGames.has(g.key);
            const active = effectiveLockedGames.has(g.key);

            return (
              <div key={g.key} role="group" aria-label={`${g.a} @ ${g.b}`}
                className={
                  "w-[168px] shrink-0 overflow-hidden rounded-xl border text-left text-xs transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 " +
                  (active
                    ? auto ? (isDark ? "border-red-500/45 bg-slate-900" : "border-red-400 bg-white") : (isDark ? "border-amber-500/45 bg-slate-900" : "border-amber-400 bg-white")
                    : isDark ? "border-slate-800 bg-slate-900 hover:border-slate-600" : "border-slate-200 bg-white hover:border-slate-400")
                }
              >
              <button type="button"
                onClick={() =>
                  setLockedGames((prev) => {
                    const next = new Set(prev);
                    if (next.has(g.key)) next.delete(g.key);
                    else next.add(g.key);
                    return next;
                  })
                }
                aria-pressed={active}
                aria-label={`${manual ? "Unlock" : "Lock"} ${g.a} @ ${g.b}${auto ? " (automatically locked)" : ""}`}
                className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400"
                title={`${g.a} @ ${g.b}${g.time ? ` · ${g.time}` : ""}`}
              >
                <div className={"flex min-h-[38px] items-center gap-2 border-b px-2 py-1.5 " + (isDark ? "border-slate-800" : "border-slate-200")}>
                  <span aria-hidden="true" className={"inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border text-[10px] " + (active ? (auto ? "border-red-500 bg-red-500 text-white" : "border-amber-500 bg-amber-500 text-slate-950") : isDark ? "border-slate-600" : "border-slate-300")}>
                    {active ? "✓" : ""}
                  </span>
                  <div className="min-w-0 flex-1 text-center leading-tight">
                    <div className={"text-[11px] font-semibold tabular-nums " + (isDark ? "text-slate-200" : "text-slate-700")}>{g.time || "Time TBD"}</div>
                    {active && <div className={"mt-0.5 text-[9px] font-medium " + (auto ? "text-red-500" : "text-amber-500")}>{manual ? "Manual lock" : "Auto lock"}</div>}
                  </div>
                  <span aria-hidden="true" className={"text-[11px] " + (active ? (auto ? "text-red-500" : "text-amber-500") : "text-slate-500")}>{active && <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-3 w-3"><rect x="3" y="7" width="10" height="7" rx="1.5" /><path d="M5 7V5a3 3 0 0 1 6 0v2" /></svg>}</span>
                </div>
              </button>
                <div className="space-y-1 p-2">
                  {[g.a, g.b].map((team) => (
                    <div key={team} className={"flex h-8 items-center gap-2 rounded-md px-2 " + (isDark ? "bg-slate-800/60" : "bg-slate-100")}>
                      <img src={teamLogo(team)} alt="" className="h-5 w-5 shrink-0 object-contain" />
                      <span className={"text-[11px] font-semibold " + (isDark ? "text-slate-100" : "text-slate-800")}>{team}</span>
                      <div className="ml-auto flex flex-col -space-y-0.5">
                        <button type="button" aria-label={`Boost ${team} up`} title={`Boost ${team} +10%`}
                          onClick={() => bumpTeamBoost(team, 1)}
                          className={"flex h-3.5 w-3.5 items-center justify-center rounded-sm text-[8px] leading-none transition hover:bg-slate-500/15 " + ((teamBoost[team] || 0) > 0 ? "text-emerald-400" : "text-slate-500")}>
                          ▲
                        </button>
                        <button type="button" aria-label={`Boost ${team} down`} title={`Boost ${team} -10%`}
                          onClick={() => bumpTeamBoost(team, -1)}
                          className={"flex h-3.5 w-3.5 items-center justify-center rounded-sm text-[8px] leading-none transition hover:bg-slate-500/15 " + ((teamBoost[team] || 0) < 0 ? "text-rose-400" : "text-slate-500")}>
                          ▼
                        </button>
                      </div>
                      <span className={"w-8 text-right text-[10px] tabular-nums " + ((teamBoost[team] || 0) > 0 ? "text-emerald-400" : (teamBoost[team] || 0) < 0 ? "text-rose-400" : "text-slate-500")}>
                        {(teamBoost[team] || 0) > 0 ? "+" : ""}{(Number(teamBoost[team]) || 0) * 10}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={panelClass + " overflow-hidden mb-4"}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 pt-3">
          <h2 className="text-sm font-semibold">Player Pool</h2>
          <span className="text-xs text-slate-500">{visiblePool.length} players shown</span>
        </div>
        <div className="px-3 pt-3">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {(site === "fd" ? ["ALL", "PG", "SG", "SF", "PF", "C", "G/F", "F/C", "UTIL"] : ["ALL", "PG", "SG", "SF", "PF", "C", "G", "F", "UTIL"]).map((p) => (
          <button
            key={p}
            onClick={() => setPosFilter(p)}
            className={
              "relative pb-2 text-xs font-semibold " +
              (posFilter === p ? "text-sky-500 after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-sky-400" : "text-slate-500 hover:text-sky-500")
            }
            style={{ padding: 0 }}
          >
            {p}
          </button>
        ))}
        <input
          className="h-8 px-3 text-sm rounded-md border border-gray-200 w-full sm:ml-auto sm:max-w-xs"
          placeholder="Search player / team / opp / pos / salary"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-3 text-xs">
          <input type="checkbox" className="h-3.5 w-3.5 accent-sky-500" checked={!showAllPlayers} onChange={() => setShowAllPlayers((v) => !v)} />
          Hide Unprojected
        </label>
        <label className="inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-3 text-xs">
          <input type="checkbox" className="h-3.5 w-3.5 accent-sky-500" checked={hideExcluded} onChange={(e) => setHideExcluded(e.target.checked)} />
          Hide Excluded
        </label>

        <button
          className="px-3 py-1 rounded-md border bg-white hover:bg-gray-50 text-xs"
          onClick={() => {
            setExcls((prev) => {
              const next = new Set(prev);
              const names = availablePool.map((r) => r.name);
              const allExcluded = names.length > 0 && names.every((n) => next.has(n));
              if (allExcluded) names.forEach((n) => next.delete(n));
              else names.forEach((n) => next.add(n));
              return next;
            });
          }}
        >
          Toggle Exclude All (Filtered)
        </button>

        <button
          className="px-3 py-1 rounded-md border bg-white hover:bg-gray-50 text-xs"
          onClick={() => {
            if (posFilter === "ALL") return;
            const names = rows
              .filter((r) => positionMatchesFilter(r.posList || [], posFilter))
              .map((r) => r.name);

            setExcls((prev) => {
              const next = new Set(prev);
              const allExcluded = names.length > 0 && names.every((n) => next.has(n));
              if (allExcluded) names.forEach((n) => next.delete(n));
              else names.forEach((n) => next.add(n));
              return next;
            });
          }}
        >
          Toggle Exclude Pos
        </button>

      </div>

        </div>

        <div className="overflow-auto" style={{ maxHeight: 520 }}>
          <table className="w-max min-w-full table-auto text-xs">
            <thead className="sticky top-0 z-20 late-table-head">
              <tr>
                <th className={th}>Lock</th>
                <th className={th}>Excl</th>
                <th className={th} onClick={() => toggleSort("posDisp")} style={{ cursor: "pointer" }}>Pos</th>
                <th className={th}>Boost</th>
                <th className={th} onClick={() => toggleSort("name")} style={{ cursor: "pointer" }}>Player</th>
                <th className={th} onClick={() => toggleSort("team")} style={{ cursor: "pointer" }}>Tm</th>
                <th className={th} onClick={() => toggleSort("opp")} style={{ cursor: "pointer" }}>Opp</th>
                <th className={th} onClick={() => toggleSort("salary")} style={{ cursor: "pointer" }}>Salary</th>
                <th className={th} onClick={() => toggleSort("time")} style={{ cursor: "pointer" }}>Time</th>
                <th className={th} onClick={() => toggleSort("eproj")} style={{ cursor: "pointer" }}>Proj</th>
                <th className={th} onClick={() => toggleSort("val")} style={{ cursor: "pointer" }}>Val</th>
                <th className={th} onClick={() => toggleSort("pown")} style={{ cursor: "pointer" }}>pOWN%</th>
                <th className={th} onClick={() => toggleSort("usedPct")} style={{ cursor: "pointer" }}>Used %</th>
                <th className={th}>Min%</th>
                <th className={th}>Max%</th>
              </tr>
            </thead>

            <tbody>
              {visiblePool.map((r, idx) => {
                const locked = locks.has(r.name);
                const excluded = excls.has(r.name) || (r.siteOnly && !unprojectedAllows.has(r.name));

                return (
                  <tr
                    key={r.key + ":" + r.name}
                    className={
                      "border-t late-zebra-row " +
                      (idx % 2 ? "late-zebra-odd " : "late-zebra-even ") +
                      (locked ? "row-locked " : "") +
                      (excluded ? "row-excluded " : "")
                    }
                  >
                    <td className={td}>
                      <input
                        type="checkbox"
                        checked={locked}
                        onChange={() =>
                          setLocks((prev) => {
                            const next = new Set(prev);
                            next.has(r.name) ? next.delete(r.name) : next.add(r.name);
                            return next;
                          })
                        }
                      />
                    </td>

                    <td className={td}>
                      <input
                        type="checkbox"
                        checked={excluded}
                        onChange={() => {
                          if (r.siteOnly) {
                            setUnprojectedAllows((prev) => {
                              const next = new Set(prev);
                              if (excluded) next.add(r.name);
                              else next.delete(r.name);
                              return next;
                            });
                            setExcls((prev) => {
                              const next = new Set(prev);
                              if (excluded) next.delete(r.name);
                              else next.add(r.name);
                              return next;
                            });
                            return;
                          }
                          setExcls((prev) => {
                            const next = new Set(prev);
                            next.has(r.name) ? next.delete(r.name) : next.add(r.name);
                            return next;
                          });
                        }}
                      />
                    </td>

                    <td className={td}>{r.posDisp}</td>

                    <td className={td}>
                      <div className="inline-flex items-center gap-1">
                        <button
                          className="w-6 h-6 rounded-md border border-gray-200 hover:bg-gray-50"
                          onClick={() =>
                            setBoost((b) => ({
                              ...b,
                              [r.name]: clamp((b[r.name] || 0) - 1, -9, 9),
                            }))
                          }
                        >
                          −
                        </button>

                        <input
                          className="h-6 px-1 text-center text-xs rounded-md border border-gray-200 w-9"
                          value={boost[r.name] ?? 0}
                          onChange={(e) =>
                            setBoost((b) => ({
                              ...b,
                              [r.name]: clamp(Math.round(+e.target.value || 0), -9, 9),
                            }))
                          }
                        />

                        <button
                          className="w-6 h-6 rounded-md border border-gray-200 hover:bg-gray-50"
                          onClick={() =>
                            setBoost((b) => ({
                              ...b,
                              [r.name]: clamp((b[r.name] || 0) + 1, -9, 9),
                            }))
                          }
                        >
                          +
                        </button>
                      </div>
                    </td>

                    <td className={td}>
                      <div className="mx-auto truncate max-w-[180px]" title={r.name}>
                        {r.name}
                        
                      </div>
                    </td>

                    <td className={td}>
                      <TeamBadge team={r.team} isDark={isDark} />
                    </td>

                    <td className={td}>
                      <TeamBadge team={r.opp} isDark={isDark} />
                    </td>

                    <td className={td}>{r.salary ? r.salary.toLocaleString() : "0"}</td>
                    <td className={td}>{r.time || ""}</td>
                    <td className={td}>
                      <div className="relative inline-flex items-center justify-center">
                        <input
                          type="number"
                          step="0.1"
                          inputMode="decimal"
                          className={
                            "h-6 px-1 text-center text-xs rounded-md border w-14 tabular-nums " +
                            (Number(r.eproj) > Number(r.originalProj || 0)
                              ? "nba-proj-up"
                              : Number(r.eproj) < Number(r.originalProj || 0)
                              ? "nba-proj-down"
                              : isDark
                              ? "bg-slate-900/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
                              : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400")
                          }
                          value={Object.prototype.hasOwnProperty.call(projDraft, r.name) ? projDraft[r.name] : Number(r.eproj || 0).toFixed(1)}
                          placeholder={(r.originalProj ?? r.proj ?? 0).toFixed(1)}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setProjDraft((prev) => ({ ...prev, [r.name]: raw }));
                            setCustomProj((prev) => {
                              const next = { ...prev };
                              if (raw.trim() === "") { delete next[r.name]; return next; }
                              const shown = Number(raw);
                              if (!Number.isFinite(shown)) return next;
                              const mult = 1 + (Number(boost[r.name]) || 0) * 0.03 + (Number(teamBoost[r.team]) || 0) * 0.10;
                              next[r.name] = String(Math.max(0, mult !== 0 ? shown / mult : shown));
                              return next;
                            });
                          }}
                          onBlur={() => setProjDraft((prev) => { const next = { ...prev }; delete next[r.name]; return next; })}
                          onFocus={(e) => e.target.select()}
                          title={
                            r.hasCustomProj
                              ? `Custom projection. Original: ${(r.originalProj ?? 0).toFixed(1)} | Current: ${(r.eproj ?? r.proj ?? 0).toFixed(1)}`
                              : "Click/type to enter a custom projection"
                          }
                        />
                        {r.hasCustomProj && (
                          <button
                            className="absolute -right-1.5 -top-1.5 h-4 w-4 rounded-full border border-gray-200 bg-white text-[10px] leading-none hover:bg-gray-50"
                            title="Reset projection"
                            onClick={() => {
                              setCustomProj((prev) => { const next = { ...prev }; delete next[r.name]; return next; });
                              setProjDraft((prev) => { const next = { ...prev }; delete next[r.name]; return next; });
                            }}
                          >
                            ↺
                          </button>
                        )}
                      </div>
                    </td>
                    <td className={td}>{(r.val || 0).toFixed(1)}</td>
                    <td className={td}>{((r.pown || 0) * 100).toFixed(1)}%</td>
                    <td className={td}>{(r.usedPct || 0).toFixed(1)}%</td>

                    <td className={td}>
                      <input
                        className="h-6 px-1 text-center text-xs rounded-md border border-gray-200 w-10"
                        value={minExp[r.name] ?? ""}
                        onChange={(e) =>
                          setMinExp((prev) => ({
                            ...prev,
                            [r.name]: normalizePctValue(e.target.value),
                          }))
                        }
                      />
                    </td>

                    <td className={td}>
                      <input
                        className="h-6 px-1 text-center text-xs rounded-md border border-gray-200 w-10"
                        value={maxExp[r.name] ?? ""}
                        onChange={(e) =>
                          setMaxExp((prev) => ({
                            ...prev,
                            [r.name]: normalizePctValue(e.target.value),
                          }))
                        }
                      />
                    </td>
                  </tr>
                );
              })}

              {!visiblePool.length && (
                <tr>
                  <td className={td} colSpan={15}>
                    No available late-swap players for the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <div className={panelClass + " overflow-hidden"}>
          <div className="px-3 py-2 border-b bg-gray-50 text-sm font-semibold">
            Player Usage (used only)
          </div>
          <div className="overflow-auto" style={{ maxHeight: 420 }}>
            <table className="min-w-full w-full text-xs">
              <thead className="sticky top-0 z-20 late-table-head">
                <tr>
                  <th className={th + " text-left"}>Player</th>
                  <th className={th}>Pos</th>
                  <th className={th}>Tm</th>
                  <th className={th}>Count</th>
                  <th className={th}>Exposure %</th>
                </tr>
              </thead>
              <tbody>
                {playerUsageRows.map((r) => (
                  <tr key={r.name} className="border-t">
                    <td className={td + " text-left"}>{r.name}</td>
                    <td className={td}>{r.pos || ""}</td>
                    <td className={td}>
                      <TeamBadge team={r.team} isDark={isDark} />
                    </td>
                    <td className={td}>{r.count}</td>
                    <td className={td}>{r.exposure.toFixed(1)}%</td>
                  </tr>
                ))}
                {!playerUsageRows.length && (
                  <tr>
                    <td className={td} colSpan={5}>Run or load a build to see usage.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={panelClass + " overflow-hidden"}>
          <div className="px-3 py-2 border-b bg-gray-50 text-sm font-semibold">
            Team Usage (used only)
          </div>
          <div className="overflow-auto" style={{ maxHeight: 420 }}>
            <table className="min-w-full w-full text-xs">
              <thead className="sticky top-0 z-20 late-table-head">
                <tr>
                  <th className={th}>Team</th>
                  <th className={th}>Count</th>
                  <th className={th}>Exposure %</th>
                </tr>
              </thead>
              <tbody>
                {teamUsageRows.map((r) => (
                  <tr key={r.team} className="border-t">
                    <td className={td}>
                      <TeamBadge team={r.team} isDark={isDark} />
                    </td>
                    <td className={td}>{r.count}</td>
                    <td className={td}>{r.exposure.toFixed(1)}%</td>
                  </tr>
                ))}
                {!teamUsageRows.length && (
                  <tr>
                    <td className={td} colSpan={3}>Run or load a build to see usage.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={panelClass + " overflow-hidden mb-4"}>
        <div className="px-3 py-2 border-b bg-gray-50 text-sm font-semibold">Imported Lineups</div>
        <div className="overflow-auto" style={{ maxHeight: 360 }}>
          <table className="min-w-[1180px] w-full text-xs">
            <thead className="sticky top-0 z-20 late-table-head">
              <tr>
                <th className={th}>#</th>
                {uploadBundle?.kind === "contest" && <th className={th}>Entry ID</th>}
                {uploadBundle?.kind === "contest" && <th className={th + " text-left"}>Contest</th>}
                <th className={th}>Locked Slots</th>
                <th className={th}>Open Slots</th>
                <th className={th}>Locked Salary</th>
                <th className={th}>Remaining Salary</th>
                <th className={th + " text-left"}>Players</th>
              </tr>
            </thead>
            <tbody>
              {hydratedImported.map((lineup) => (
                <tr key={lineup.id} className="border-t">
                  <td className={td}>{lineup.index}</td>
                  {uploadBundle?.kind === "contest" && <td className={td}>{lineup.entryId || "—"}</td>}
                  {uploadBundle?.kind === "contest" && <td className={td + " text-left"}>{lineup.contestName || "—"}</td>}
                  <td className={td}>{lineup.lockedAssignment.length}</td>
                  <td className={td}>{lineup.openSlots.length}</td>
                  <td className={td}>{lineup.lockedSalary.toLocaleString()}</td>
                  <td className={td}>{lineup.remainingSalary.toLocaleString()}</td>
                  <td className={td + " text-left"}>
                    <div className="flex flex-wrap gap-1">
                      {lineup.assignment.map((a) => (
                        <span
                          key={`${lineup.id}:${a.slot.replace(/[12]$/, "")}`}
                          className={
                            "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 " +
                            (a.locked
                              ? "bg-red-50 border-red-300 text-red-700"
                              : "bg-blue-50 border-blue-200 text-blue-700")
                          }
                        >
                          <span className="font-semibold">{a.slot.replace(/[12]$/, "")}</span>
                          <span>{a.name || "—"}</span>
                          {a.locked && <span>🔒</span>}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}

              {!hydratedImported.length && (
                <tr>
                  <td className={td} colSpan={uploadBundle?.kind === "contest" ? 8 : 6}>
                    Upload a lineup CSV or contest entries CSV to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold">Swapped Results</div>
        <div className="text-xs text-gray-600">
          {uploadBundle?.kind === "contest"
            ? "Selected contest rows will be updated on export; all other contest rows stay untouched."
            : "Preserves lineup order and locked slot positions exactly."}
        </div>
      </div>

      <div className={panelClass + " overflow-hidden mb-4"}>
        <div className="overflow-auto" style={{ maxHeight: 420 }}>
          <table className="min-w-[1420px] w-full text-xs">
            <thead className="sticky top-0 z-20 late-table-head">
              <tr>
                <th className={th}>#</th>
                {uploadBundle?.kind === "contest" && <th className={th}>Entry ID</th>}
                <th className={th}>Changed</th>
                <th className={th}>Salary</th>
                <th className={th}>Proj</th>
                <th className={th}>pOWN%</th>
                <th className={th}>Avg USG%</th>
                <th className={th + " text-left"}>Final Players</th>
              </tr>
            </thead>
            <tbody>
              {visibleSwapped.map((lineup) => (
                <tr key={`swap:${lineup.id}`} className="border-t">
                  <td className={td}>{lineup.index}</td>
                  {uploadBundle?.kind === "contest" && <td className={td}>{lineup.entryId || "—"}</td>}
                  <td className={td}>{lineup.changed ? "Yes" : "No"}</td>
                  <td className={td}>{(lineup.finalSalary || 0).toLocaleString()}</td>
                  <td className={td}>{(lineup.finalProj || 0).toFixed(1)}</td>
                  <td className={td}>{(lineup.finalPown || 0).toFixed(1)}</td>
                  <td className={td}>{(lineup.finalAvgUsg || 0).toFixed(1)}%</td>
                  <td className={td + " text-left"}>
                    <div className="flex flex-wrap gap-1">
                      {(lineup.finalAssignment || []).map((a) => (
                        <span
                          key={`${lineup.id}:${a.slot.replace(/[12]$/, "")}:final`}
                          className={
                            "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 " +
                            (a.locked
                              ? "bg-red-50 border-red-300 text-red-700"
                              : "bg-blue-50 border-blue-200 text-blue-700")
                          }
                        >
                          <span className="font-semibold">{a.slot.replace(/[12]$/, "")}</span>
                          <span>{a.name || "—"}</span>
                          {a.locked && <span>🔒</span>}
                        </span>
                      ))}
                    </div>
                    {!!lineup.backendError && (
                      <div className="mt-1 text-[11px] text-red-600">{lineup.backendError}</div>
                    )}
                  </td>
                </tr>
              ))}

              {!visibleSwapped.length && (
                <tr>
                  <td className={td} colSpan={uploadBundle?.kind === "contest" ? 8 : 7}>
                    Run Late Swap to generate swapped results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={panelClass + " overflow-hidden mb-4"}>
        <div className="px-3 py-2 border-b bg-gray-50 text-sm font-semibold flex items-center justify-between gap-2 flex-wrap">
          <span>Saved Builds</span>
          <button
            className="h-8 px-3 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-xs"
            onClick={() => setSavedBuilds([])}
            disabled={!savedBuilds.length}
          >
            Clear Saved Builds
          </button>
        </div>

        <div className="overflow-auto" style={{ maxHeight: 260 }}>
          <table className="min-w-[980px] w-full text-xs">
            <thead className="sticky top-0 z-20 late-table-head">
              <tr>
                <th className={th}>Time</th>
                <th className={th}>Site</th>
                <th className={th}>Slate</th>
                <th className={th}>Type</th>
                <th className={th + " text-left"}>Contest</th>
                <th className={th}>Lineups</th>
                <th className={th}>Changed</th>
                <th className={th}>Errors</th>
                <th className={th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {savedBuilds.map((b) => (
                <tr key={b.id} className="border-t">
                  <td className={td}>{new Date(b.createdAt).toLocaleString()}</td>
                  <td className={td}>{String(b.site || "").toUpperCase()}</td>
                  <td className={td}>{String(b.slate || "").toUpperCase()}</td>
                  <td className={td}>{String(b.uploadKind || "").toUpperCase()}</td>
                  <td className={td + " text-left"}>{b.selectedContestName || "—"}</td>
                  <td className={td}>{b.lineupCount}</td>
                  <td className={td}>{b.changedCount}</td>
                  <td className={td}>{b.errorCount}</td>
                  <td className={td}>
                    <button
                      className="px-2 py-1 rounded-md border bg-white hover:bg-gray-50 text-xs"
                      onClick={() => {
                        setSwappedLineups(b.results || []);
                        setStatusMsg(`Loaded saved build from ${new Date(b.createdAt).toLocaleString()}.`);
                      }}
                    >
                      Load
                    </button>
                  </td>
                </tr>
              ))}
              {!savedBuilds.length && (
                <tr>
                  <td className={td} colSpan={9}>No saved builds yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {visibleSwapped.map((lineup) => (
          <div key={`card:${lineup.id}`} className="rounded-2xl border border-gray-200 bg-white shadow p-3">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-base font-semibold">
                Lineup #{lineup.index}
                {uploadBundle?.kind === "contest" && lineup.entryId ? ` · ${lineup.entryId}` : ""}
              </div>
              <div className="text-[11px] text-gray-600">
                {lineup.changed ? "Changed" : "Unchanged"}
              </div>
            </div>

            <table className="w-full text-xs">
              <thead className="text-gray-600">
                <tr>
                  <th className="text-left py-1">Slot</th>
                  <th className="text-left">Player</th>
                  <th className="text-right">Proj</th>
                  <th className="text-right">pOWN%</th>
                  <th className="text-right">Sal</th>
                </tr>
              </thead>
              <tbody>
                {(lineup.finalAssignment || []).map((a, j) => (
                  <tr key={`${lineup.id}:${a.slot.replace(/[12]$/, "")}:${j}`} className="border-t">
                    <td className="py-1">{a.slot.replace(/[12]$/, "")}</td>
                    <td className="py-1">
                      <div className="flex items-center gap-2">
                        {a.team && (
                          <img
                            src={teamLogo(a.team)}
                            alt={a.team}
                            title={a.team}
                            className="inline-block align-middle w-5 h-5 shrink-0 object-contain"
                            style={{ width: 20, height: 20 }}
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        )}
                        <span className="whitespace-nowrap">{a.name || "—"}</span>
                        {a.locked && <span title="Locked">🔒</span>}
                      </div>
                    </td>
                    <td className="py-1 text-right">{(a.proj || 0).toFixed(1)}</td>
                    <td className="py-1 text-right">{((a.pown || 0) * 100).toFixed(1)}</td>
                    <td className="py-1 text-right">{(a.salary || 0).toLocaleString()}</td>
                  </tr>
                ))}
                <tr className="border-t font-semibold">
                  <td className="py-1">Totals</td>
                  <td />
                  <td className="py-1 text-right">{(lineup.finalProj || 0).toFixed(1)}</td>
                  <td className="py-1 text-right">{(lineup.finalPown || 0).toFixed(1)}</td>
                  <td className="py-1 text-right">{(lineup.finalSalary || 0).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}